import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Edit3, Search, Save, X, ImageIcon, MapPin, Globe, Upload,
  Plus, ChevronRight, AlertCircle, RefreshCw, Sparkles, Brain, Bot, GraduationCap, Trash2,
} from 'lucide-react';
import {
  getExperiences,
  getExperienceById,
  updateExperience,
  createExperience,
  hideExperience,
  getHiddenExperienceIds,
  filterHiddenExperiences,
  invalidateExperiencesListCache,
  getInteractionsByExperience,
  createInteraction,
  updateInteraction,
  deleteInteraction,
} from '../services/legacyApiService';
import { LegacyExperience, LegacyInteraction } from '../types/legacy';
import { uploadExperienceCover } from '../services/uploadService';
import {
  applyExperienceCoverOverrides,
  resolveExperienceCoverCandidates,
  resolveAvatarUrl,
  setExperienceCoverOverride,
} from '../utils/imageUtils';
import MapCoordinatePicker from '../components/MapCoordinatePicker';
import ExperienceTranslationsPanel from '../components/ExperienceTranslationsPanel';
import ExperienceKnowledgeMediaPanel from '../components/ExperienceKnowledgeMediaPanel';
import PromptsGeniusLoci from './PromptsGeniusLoci';
import {
  autofillExperienceFields,
  generateExperiencePois,
  GeneratedExperiencePoi,
} from '../services/geminiGenerateService';
import { ApeConfig, getApeConfig } from '../services/apeConfigService';
import {
  applyExperienceContinuityDefaults,
  computeExperienceFlowStatus,
} from '../utils/experienceFlow';
import {
  getExperienceGeniusOverride,
  saveExperienceGeniusOverride,
  type ExperienceGeniusOverride,
} from '../services/experienceGeniusOverrideService';

const emptyEditState: Omit<LegacyExperience, 'id'> = {
  name: '',
  city: '',
  description: '',
  nomeChatbot: '',
  frase_avvio_percorso: '',
  messIntermedio: '',
  messFinale: '',
  isPublished: false,
  duration: undefined,
  distance: undefined,
  accessibility: false,
  isKids: false,
  isAtHome: false,
  isOnTheSite: false,
  latitude: null,
  longitude: null,
  tutorDescription: '',
  tutorPersonality: '',
  tutorKnowledge: '',
  geminiPrompt: '',
  recoveryPrompt: '',
  tutorLanguageRegister: '',
  tutorEpoch: '',
  responseLength: 'medium',
  routePlan: '',
  language: 'it',
  voce: undefined,
  idPersonality: undefined,
};

type ExperienceTab = 'base' | 'chatbot' | 'prompt' | 'knowledge' | 'poi' | 'media';

type PoiDraft = GeneratedExperiencePoi & {
  selected: boolean;
  sourceInteractionId?: number | null;
  sourceDialogflowIntentId?: string;
  sourceAction?: string | null;
  dirty?: boolean;
};

const EXP_SAVE_DEBUG = String(import.meta.env.VITE_EXP_SAVE_DEBUG || '').trim().toLowerCase() === 'true';

const EMPTY_APE_CONFIG: ApeConfig = {
  level0Prompt: '',
  level6Prompt: '',
  generationPromptTemplate: '',
  level0Enabled: false,
  level6Enabled: false,
};

const normalizeText = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const toActionToken = (value: string): string => {
  const token = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return token || `generated.${Date.now()}`;
};

const toDialogflowDisplayNameSafe = (value: string): string => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._ -]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.slice(0, 90) || `welcome-${Date.now().toString(36)}`;
};

const sanitizePoiText = (value: unknown, maxLength: number): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, maxLength);

const normalizeResponseLength = (value: unknown): 'short' | 'medium' | 'long' => {
  const normalized = String(value || '').trim();
  return normalized === 'short' || normalized === 'medium' || normalized === 'long'
    ? normalized
    : 'medium';
};

function buildExperienceFlagChips(experience: Partial<LegacyExperience>) {
  const chips: Array<{ key: string; label: string; className: string }> = [];
  const duration = Number(experience.duration);
  const distance = Number(experience.distance);

  if (Number.isFinite(duration) && duration > 0) {
    chips.push({
      key: 'duration',
      label: `${Math.round(duration)} min`,
      className: 'bg-blue-50 text-blue-600',
    });
  }
  if (Number.isFinite(distance) && distance > 0) {
    chips.push({
      key: 'distance',
      label: `${distance} km`,
      className: 'bg-emerald-50 text-emerald-600',
    });
  }
  if (experience.isKids) {
    chips.push({
      key: 'kids',
      label: 'Per famiglie',
      className: 'bg-amber-50 text-amber-700',
    });
  }
  if (experience.accessibility) {
    chips.push({
      key: 'accessibility',
      label: 'Accessibile',
      className: 'bg-violet-50 text-violet-700',
    });
  }
  if (experience.isOnTheSite) {
    chips.push({
      key: 'onsite',
      label: 'Sul posto',
      className: 'bg-sky-50 text-sky-700',
    });
  }
  if (experience.isAtHome) {
    chips.push({
      key: 'athome',
      label: 'Da casa',
      className: 'bg-fuchsia-50 text-fuchsia-700',
    });
  }

  return chips;
}

const POI_LIMITS = {
  displayName: 90,
  description: 460,
  message: 360,
  quickReply: 84,
  action: 110,
  mediaName: 90,
  mediaLink: 255,
} as const;

const toFiniteNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const hasInvalidPoiCoordinates = (latitude: number, longitude: number): boolean => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return true;
  if (latitude < -90 || latitude > 90) return true;
  if (longitude < -180 || longitude > 180) return true;
  // 0,0 is a common geocoding fallback and is rejected by legacy backend for POI use-cases.
  if (Math.abs(latitude) < 0.000001 && Math.abs(longitude) < 0.000001) return true;
  return false;
};

const buildPoiDedupeKeys = (poi: Partial<PoiDraft>): string[] => {
  const keys = [
    poi.sourceInteractionId ? `source:${poi.sourceInteractionId}` : '',
    poi.sourceAction ? `action:${normalizeText(String(poi.sourceAction))}` : '',
    poi.name ? `name:${normalizeText(String(poi.name))}` : '',
  ];
  const latitude = toFiniteNumberOrNull(poi.latitude);
  const longitude = toFiniteNumberOrNull(poi.longitude);
  if (latitude != null && longitude != null && !hasInvalidPoiCoordinates(latitude, longitude)) {
    keys.push(`geo:${latitude.toFixed(5)}|${longitude.toFixed(5)}`);
    if (poi.name) {
      keys.push(`namegeo:${normalizeText(String(poi.name))}|${latitude.toFixed(5)}|${longitude.toFixed(5)}`);
    }
  }
  return keys.filter(Boolean);
};

const mergeGeneratedPoiDrafts = (
  currentDrafts: PoiDraft[],
  generatedDrafts: PoiDraft[],
): { drafts: PoiDraft[]; added: number; skipped: number } => {
  const existingKeys = new Set(currentDrafts.flatMap(buildPoiDedupeKeys));
  const additions: PoiDraft[] = [];
  let skipped = 0;

  generatedDrafts.forEach((draft) => {
    const keys = buildPoiDedupeKeys(draft);
    const duplicate = keys.some((key) => existingKeys.has(key));
    if (duplicate) {
      skipped += 1;
      return;
    }
    additions.push(draft);
    keys.forEach((key) => existingKeys.add(key));
  });

  return {
    drafts: [...currentDrafts, ...additions],
    added: additions.length,
    skipped,
  };
};

const isLegacyInvalidAvatarValue = (value: string): boolean => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return true;
  return (
    normalized === 'default.png'
    || normalized === 'default.jpg'
    || normalized === 'default.webp'
    || normalized === 'null'
    || normalized === 'undefined'
    || /^\d{10,}\.(png|jpe?g|webp)$/i.test(normalized)
  );
};

const normalizeComparableAssetValue = (value: string): string => {
  const normalized = String(value || '')
    .trim()
    .replace(/[?#].*$/, '')
    .replace(/^https?:\/\/platform\.glooci\.it\//i, '')
    .replace(/^\/+/g, '')
    .replace(/^storage\//i, '')
    .replace(/^avatar\//i, '')
    .replace(/^experiences\//i, '');
  return normalized.toLowerCase();
};

const shouldSyncAvatarWithCover = (params: {
  avatar: string;
  previousCoverSources: string[];
  previousCoverCandidates: string[];
}): boolean => {
  const avatarRaw = String(params.avatar || '').trim();
  if (isLegacyInvalidAvatarValue(avatarRaw)) return true;

  const normalizedAvatar = normalizeComparableAssetValue(avatarRaw);
  const normalizedAvatarFile = normalizedAvatar.split('/').pop() || '';
  const normalizedCoverSources = params.previousCoverSources
    .map((item) => normalizeComparableAssetValue(item))
    .filter(Boolean);

  if (normalizedAvatar && normalizedCoverSources.includes(normalizedAvatar)) return true;
  if (normalizedAvatarFile && normalizedCoverSources.some((item) => item.split('/').pop() === normalizedAvatarFile)) {
    return true;
  }

  const avatarUrl = resolveAvatarUrl(avatarRaw);
  if (avatarUrl && params.previousCoverCandidates.some((candidate) => candidate === avatarUrl)) return true;

  return false;
};

const extractVoiceCodeFromPrompt = (rawPrompt: string): string => {
  const normalized = String(rawPrompt || '');
  const match = normalized.match(/it-IT-(?:Standard|Wavenet|Neural2)-[A-Z]/i);
  return match ? String(match[0]).trim() : '';
};

const buildAutoSystemPromptFromAutofill = (params: {
  characterName?: string;
  roleOrStory?: string;
  tone?: string;
  place?: string;
  experienceName?: string;
  experienceDescription?: string;
  historicalEpoch?: string;
  archetype?: string;
  knowledge?: string;
  voiceCode?: string;
}): string => {
  const characterName = String(params.characterName || '').trim() || 'Guida del territorio';
  const roleOrStory = String(params.roleOrStory || '').trim() || 'figura narrante storica legata al territorio';
  const tone = String(params.tone || '').trim() || 'coinvolgente, immersivo, coerente con il personaggio';
  const place = String(params.place || '').trim() || 'questo territorio';
  const experienceName = String(params.experienceName || '').trim() || 'non definita';
  const description = String(params.experienceDescription || '').trim() || 'non disponibile';
  const epoch = String(params.historicalEpoch || '').trim();
  const archetype = String(params.archetype || '').trim();
  const knowledge = String(params.knowledge || '').trim();
  const voiceCode = String(params.voiceCode || '').trim();

  return `Impersona "${characterName}", ${roleOrStory}.

Parla sempre in prima persona, con tono coerente al tuo carattere: ${tone}.

Sei una guida turistica completa del territorio di ${place}.

---

UTILIZZO DELLA RICERCA ONLINE (GOOGLE GROUNDING)

Quando necessario:
- Effettua ricerche online per ottenere informazioni aggiornate, precise e rilevanti.
- Integra le informazioni nel racconto in modo naturale.
- Rielabora sempre i contenuti, senza copiarli.
- Non citare mai esplicitamente fonti o link.

---

AMBITO DI CONOSCENZA

Puoi parlare liberamente di:
- Storia del luogo.
- Monumenti, luoghi e punti di interesse.
- Natura e paesaggio.
- Cultura e tradizioni locali.
- Eventi, attività ed esperienze.
- Gastronomia e prodotti tipici.
- Vita quotidiana e identità del territorio.
- Suggerimenti di visita e itinerari.
- Attività adatte a famiglie o gruppi.

---

STILE

- Mantieni sempre coerenza con il personaggio.
- Usa un linguaggio coinvolgente e immersivo.
- Adatta il livello di complessità all'utente.
- Quando possibile, guida l'utente con suggerimenti o domande.

---

REGOLE (GUARDRAIL)

- Rimani sempre coerente con il personaggio e il territorio.
- Evita argomenti non pertinenti al contesto.
- Non dichiarare mai di essere un'intelligenza artificiale.
- Non uscire mai dal ruolo assegnato.
- Se l'utente esce dal contesto, riporta la conversazione sul territorio dicendo qualcosa di coerente con il personaggio.

---

IMPORTANTE

Se utilizzi informazioni ottenute tramite ricerca online:
- Integrale sempre nel racconto.
- Mantieni naturalezza e coerenza narrativa.
- Non interrompere mai l'immersione del personaggio.

CONTESTO ESPERIENZA:
- Esperienza: ${experienceName}
- Città/luogo: ${place}
- Descrizione esperienza: ${description}
${epoch ? `- Epoca storica di riferimento: ${epoch}` : ''}
${archetype ? `- Archetipo / personalità: ${archetype}` : ''}
${voiceCode ? `- Impostazione voce TTS (APE L3): voice_it="${voiceCode}"` : ''}
${knowledge ? `- Base conoscenza verificata:\n${knowledge}` : ''}
`.trim();
};

const extractInteractionPrimaryMessage = (interaction: LegacyInteraction): string => {
  const fromMessages = (interaction.messagesDTO || [])
    .flatMap((message) => message?.textStringDTOList || [])
    .map((row) => sanitizePoiText(row?.text || '', POI_LIMITS.message))
    .find(Boolean);
  if (fromMessages) return fromMessages;
  return sanitizePoiText(
    interaction.description || interaction.displayName || 'Informazione disponibile.',
    POI_LIMITS.message,
  );
};

const extractInteractionQuickReplies = (interaction: LegacyInteraction): string[] => {
  const fromMessages = (interaction.messagesDTO || [])
    .flatMap((message) => message?.quickRepliesDTO?.quickTextDTOList || [])
    .map((row) => sanitizePoiText(row?.text || '', POI_LIMITS.quickReply))
    .filter(Boolean);
  return dedupeArray(fromMessages).slice(0, 6);
};

const extractInteractionMapsUrl = (interaction: LegacyInteraction): string => {
  const fromMessages = (interaction.messagesDTO || [])
    .flatMap((message) => message?.mediaDTO?.links || [])
    .map((value) => sanitizePoiText(value, POI_LIMITS.mediaLink))
    .find(Boolean);
  return fromMessages || '';
};

const isPoiInteraction = (interaction: LegacyInteraction): boolean => {
  const action = String(interaction.action || '').toLowerCase().trim();
  // POI interactions are always created with action starting with "poi."
  return action.startsWith('poi.');
};

const hasGeoCoordinates = (interaction: Partial<LegacyInteraction> | null | undefined): boolean => {
  const latitude = toFiniteNumberOrNull(interaction?.latitude);
  const longitude = toFiniteNumberOrNull(interaction?.longitude);
  return latitude != null && longitude != null;
};

const isPoiLikeInteraction = (interaction: LegacyInteraction): boolean =>
  !isStartLikeIntent(interaction) && (isPoiInteraction(interaction) || hasGeoCoordinates(interaction));

const buildPoiDraftsFromInteractions = (interactions: LegacyInteraction[]): PoiDraft[] => {
  const seen = new Set<string>();
  return (interactions || [])
    .filter(isPoiLikeInteraction)
    .map((interaction, index) => {
      const latitude = toFiniteNumberOrNull(interaction.latitude);
      const longitude = toFiniteNumberOrNull(interaction.longitude);
      if (latitude == null || longitude == null) return null;

      const name = sanitizePoiText(interaction.displayName || '', POI_LIMITS.displayName);
      if (!name) return null;

      const key = normalizeText(`${name}|${latitude}|${longitude}`);
      if (!key || seen.has(key)) return null;
      seen.add(key);

      const summary = extractInteractionPrimaryMessage(interaction);
      const description = sanitizePoiText(interaction.description || summary, POI_LIMITS.description);
      const address = normalizeText(description) !== normalizeText(summary) ? description : '';
      const mapsUrl = extractInteractionMapsUrl(interaction);

      return {
        id: `saved_${interaction.id || index}_${key}`,
        sourceInteractionId: Number(interaction.id || 0) || null,
        sourceDialogflowIntentId: interaction.dialogflowIntentId || '',
        sourceAction: interaction.action || null,
        name,
        address,
        latitude,
        longitude,
        mapsUrl,
        summary,
        suggestedQueries: extractInteractionQuickReplies(interaction),
        selected: false,
      } satisfies PoiDraft;
    })
    .filter(Boolean) as PoiDraft[];
};

const createEmptyManualPoiDraft = (experienceLabel: string): PoiDraft => {
  const nonce = Date.now().toString(36);
  return {
    id: `manual_${nonce}`,
    name: '',
    address: '',
    latitude: null,
    longitude: null,
    mapsUrl: '',
    summary: `POI manuale per ${experienceLabel}`.slice(0, POI_LIMITS.message),
    suggestedQueries: [],
    selected: true,
  };
};

const buildUniquePoiAction = (
  experienceId: number,
  displayName: string,
  existingActions: Set<string>,
): string => {
  const nonce = Math.random().toString(36).slice(2, 6);
  const slug = toActionToken(displayName);
  const base = sanitizePoiText(
    `poi.${experienceId}.${slug || 'generated'}-${nonce}`,
    POI_LIMITS.action,
  );
  let candidate = base || sanitizePoiText(`poi.${experienceId}.generated`, POI_LIMITS.action);
  let index = 2;
  while (existingActions.has(normalizeText(candidate))) {
    candidate = sanitizePoiText(`${base}-${index}`, POI_LIMITS.action);
    index += 1;
  }
  return candidate;
};

const dedupeArray = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

const START_INTENT_ACTION = 'avviata';
const START_EVENT_NAMES = ['start', 'input.welcome', 'welcome'] as const;
const INITIAL_MESSAGE_PLACEHOLDERS = new Set([
  'start',
  'input.welcome',
  'welcome',
  'avviata',
  'avvio',
  'inizio',
  'inizia',
]);

const normalizeIntentToken = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizeInitialMessageToken = (value: unknown): string =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const isMeaningfulInitialMessage = (
  value: unknown,
  experience?: Partial<LegacyExperience> | null,
): boolean => {
  const normalized = normalizeInitialMessageToken(value);
  if (!normalized || INITIAL_MESSAGE_PLACEHOLDERS.has(normalized)) return false;
  const intermediate = normalizeInitialMessageToken(experience?.messIntermedio);
  if (intermediate && normalized === intermediate) return false;
  return true;
};

const pickInitialMessageCandidate = (
  ...sources: Array<Partial<LegacyExperience> | ExperienceGeniusOverride | Record<string, unknown> | null | undefined>
): string => {
  const keys = [
    'frase_avvio_percorso',
    'welcomeMessage',
    'welcome_message',
    'initialMessage',
    'startMessage',
    'messaggioIniziale',
    'messaggio_iniziale',
  ];
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const record = source as Record<string, unknown>;
    for (const key of keys) {
      const candidate = String(record[key] || '').trim();
      if (isMeaningfulInitialMessage(candidate, source as Partial<LegacyExperience>)) {
        return candidate;
      }
    }
    const nested = record.experience;
    if (nested && typeof nested === 'object') {
      const nestedCandidate = pickInitialMessageCandidate(nested as Record<string, unknown>);
      if (nestedCandidate) return nestedCandidate;
    }
  }
  return '';
};

const extractInteractionPrimaryText = (
  interaction: Partial<LegacyInteraction> | null | undefined,
): string => {
  if (!interaction || !Array.isArray(interaction.messagesDTO)) return '';
  for (const message of interaction.messagesDTO) {
    const texts = Array.isArray(message?.textStringDTOList) ? message.textStringDTOList : [];
    for (const textRow of texts) {
      const text = String(textRow?.text || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
  }
  return '';
};

const extractStartIntentInitialMessage = (
  interaction: Partial<LegacyInteraction> | null | undefined,
  experience?: Partial<LegacyExperience> | null,
): string => {
  if (!interaction) return '';
  const description = String(interaction.description || '').replace(/\s+/g, ' ').trim();
  const messageText = extractInteractionPrimaryText(interaction);
  const candidates = [description, messageText];
  return candidates.find((candidate) => isMeaningfulInitialMessage(candidate, experience)) || '';
};

const isStartLikeIntent = (interaction: Partial<LegacyInteraction> | null | undefined): boolean => {
  if (!interaction) return false;
  const displayName = normalizeIntentToken(interaction.displayName);
  const action = normalizeIntentToken(interaction.action);
  const events = (Array.isArray(interaction.eventsDTO) ? interaction.eventsDTO : [])
    .map((event) => normalizeIntentToken(event?.name));
  return (
    events.includes('start')
    || events.includes('input.welcome')
    || events.includes('welcome')
    || action.includes('start')
    || action.includes('welcome')
    || action.includes('avviata')
    || displayName.includes('start')
    || displayName.includes('welcome')
    || displayName.includes('iniz')
  );
};

const resolveInitialMessageText = (experience: Partial<LegacyExperience>): string => {
  const explicit = pickInitialMessageCandidate(experience);
  if (explicit) return explicit;
  const name = String(experience.nomeChatbot || '').trim();
  const experienceName = String(experience.name || '').trim();
  if (name && experienceName) {
    return `Ciao, sono ${name}. Benvenuto in ${experienceName}, iniziamo il percorso.`;
  }
  return '';
};

const buildPoiTrainingPhrases = (
  displayName: string,
  quickReplies: string[],
): LegacyInteraction['trainingPhraseDTO'] => {
  const candidates = dedupeArray([
    sanitizePoiText(displayName, POI_LIMITS.displayName),
    ...quickReplies.map((value) => sanitizePoiText(value, POI_LIMITS.quickReply)),
  ])
    .filter(Boolean)
    .slice(0, 8);

  return candidates.map((text) => ({
    type: '1',
    partsListDTO: [{ text }],
  }));
};

const buildPoiMessagePayload = (params: {
  messageText: string;
  quickReplies: string[];
  mapsUrl: string;
  displayName: string;
}): LegacyInteraction['messagesDTO'] => {
  const compactText =
    sanitizePoiText(params.messageText, POI_LIMITS.message)
    || `POI: ${sanitizePoiText(params.displayName, POI_LIMITS.displayName) || 'Punto di interesse'}`;
  const mapsUrl = sanitizePoiText(params.mapsUrl, POI_LIMITS.mediaLink);
  const quickReplies = dedupeArray(
    (params.quickReplies || [])
      .map((value) => sanitizePoiText(value, POI_LIMITS.quickReply))
      .filter(Boolean),
  ).slice(0, 8);

  return [{
    textStringDTOList: [{ text: compactText }],
    quickRepliesDTO: quickReplies.length > 0
      ? { quickTextDTOList: quickReplies.map((text) => ({ text })) }
      : null,
    mediaDTO: mapsUrl
      ? {
        name: sanitizePoiText(params.displayName, POI_LIMITS.mediaName) || 'Mappa',
        links: [mapsUrl],
      }
      : null,
  }];
};

const ExperiencesNew = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedExperienceId = Number(searchParams.get('experienceId') || '');
  const requestedExperienceIdSafe = Number.isFinite(requestedExperienceId) && requestedExperienceId > 0
    ? requestedExperienceId
    : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [experiences, setExperiences] = useState<LegacyExperience[]>([]);
  const [editing, setEditing] = useState<LegacyExperience | null>(null);
  const [editForm, setEditForm] = useState<Omit<LegacyExperience, 'id'>>(emptyEditState);
  const [loadedGeniusOverride, setLoadedGeniusOverride] = useState<ExperienceGeniusOverride>({});
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [deletingExperienceId, setDeletingExperienceId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<ExperienceTab>('base');
  const [autoFillLocation, setAutoFillLocation] = useState('');
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillError, setAutoFillError] = useState<string | null>(null);
  const [poiPrompt, setPoiPrompt] = useState('');
  const [poiDrafts, setPoiDrafts] = useState<PoiDraft[]>([]);
  const [poiError, setPoiError] = useState<string | null>(null);
  const [poiInfo, setPoiInfo] = useState<string | null>(null);
  const [poiGenerating, setPoiGenerating] = useState(false);
  const [poiSaving, setPoiSaving] = useState(false);
  const [poiDeletingId, setPoiDeletingId] = useState<string | null>(null);
  const [selectedPoiMapId, setSelectedPoiMapId] = useState<string | null>(null);
  const [experienceInteractions, setExperienceInteractions] = useState<LegacyInteraction[]>([]);
  const [apeConfig, setApeConfig] = useState<ApeConfig>(EMPTY_APE_CONFIG);

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverDropActive, setCoverDropActive] = useState(false);
  const [brokenMediaUrls, setBrokenMediaUrls] = useState<Record<string, true>>({});

  const load = async (searchText?: string, { forceRefresh = false }: { forceRefresh?: boolean } = {}) => {
    try {
      setLoading(true);
      setError(null);
      if (forceRefresh) {
        invalidateExperiencesListCache();
      }
      const [data, hiddenIds] = await Promise.all([
        getExperiences(searchText, { forceRefresh }),
        getHiddenExperienceIds({ forceRefresh: true }).catch(() => [] as number[]),
      ]);
      const visible = filterHiddenExperiences(data, hiddenIds);
      setExperiences(applyExperienceCoverOverrides(visible));
    } catch (err) {
      console.error(err);
      setError('Impossibile caricare le esperienze. Controlla la connessione al server Glooci.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(undefined, { forceRefresh: true }); }, []);
  useEffect(() => {
    void (async () => {
      const config = await getApeConfig();
      setApeConfig(config);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return experiences;
    const q = query.toLowerCase();
    return experiences.filter((exp) =>
      [exp.name, exp.city, exp.description, exp.nomeChatbot]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [experiences, query]);

  const refreshInteractions = async (experienceId: number): Promise<LegacyInteraction[]> => {
    if (!Number.isFinite(experienceId) || experienceId <= 0) {
      setExperienceInteractions([]);
      return [];
    }
    try {
      const data = await getInteractionsByExperience({ idExperience: experienceId });
      setExperienceInteractions(data);
      return data;
    } catch (err) {
      console.warn('[ExperiencesNew] refreshInteractions error', err);
      return experienceInteractions;
    }
  };

  const persistedExperienceId = !isCreating ? Number(editing?.id || 0) : 0;
  const hasPersistedExperienceId = Number.isFinite(persistedExperienceId) && persistedExperienceId > 0;

  const updatePoiDraft = (poiId: string, patch: Partial<PoiDraft>) => {
    setPoiDrafts((prev) => prev.map((item) => {
      if (item.id !== poiId) return item;
      const explicitSelectionOnly =
        Object.keys(patch).length === 1 &&
        Object.prototype.hasOwnProperty.call(patch, 'selected');
      return {
        ...item,
        ...patch,
        selected: patch.selected ?? true,
        dirty: explicitSelectionOnly ? item.dirty : true,
      };
    }));
  };

  const addManualPoiDraft = () => {
    const experienceLabel = String(editForm.name || '').trim() || 'esperienza';
    const draft = createEmptyManualPoiDraft(experienceLabel);
    setPoiDrafts((prev) => [draft, ...prev]);
    setSelectedPoiMapId(draft.id);
    setPoiInfo('POI manuale aggiunto. Compila, scegli la posizione sulla mappa e premi Salva POI.');
    setPoiError(null);
  };

  const removePoiDraft = async (poi: PoiDraft) => {
    const removeLocalDraft = () => {
      setPoiDrafts((prev) => prev.filter((item) => item.id !== poi.id));
      setSelectedPoiMapId((current) => (current === poi.id ? null : current));
    };

    setPoiError(null);
    setPoiInfo(null);

    if (!poi.sourceInteractionId && !poi.sourceDialogflowIntentId) {
      removeLocalDraft();
      setPoiInfo('POI rimosso dalla bozza.');
      return;
    }

    const displayName = sanitizePoiText(poi.name, POI_LIMITS.displayName) || 'questo POI';
    const confirmed = window.confirm(`Eliminare "${displayName}" dalla piattaforma? L'intent collegato verrà rimosso.`);
    if (!confirmed) return;

    try {
      setPoiDeletingId(poi.id);
      await deleteInteraction({
        id: Number(poi.sourceInteractionId || 0),
        dialogflowIntentId: poi.sourceDialogflowIntentId || '',
      });
      removeLocalDraft();
      if (hasPersistedExperienceId) {
        const refreshedInteractions = await refreshInteractions(persistedExperienceId);
        const refreshedPoiDrafts = buildPoiDraftsFromInteractions(refreshedInteractions);
        setPoiDrafts(refreshedPoiDrafts);
        setSelectedPoiMapId((current) => {
          if (current && refreshedPoiDrafts.some((item) => item.id === current)) return current;
          return refreshedPoiDrafts[0]?.id || null;
        });
      }
      setPoiInfo(`POI eliminato: ${displayName}.`);
    } catch (err) {
      console.error(err);
      setPoiError(`Eliminazione POI non riuscita per "${displayName}". Verifica permessi e intent Dialogflow.`);
    } finally {
      setPoiDeletingId(null);
    }
  };

  const selectedPoi = useMemo(
    () => poiDrafts.find((item) => item.id === selectedPoiMapId) || null,
    [poiDrafts, selectedPoiMapId],
  );

  const mergePoiDraftsAfterRefresh = (
    refreshedPoiDrafts: PoiDraft[],
    previousDrafts: PoiDraft[],
  ): PoiDraft[] => {
    const refreshedKeys = new Set(
      refreshedPoiDrafts
        .map((poi) => [
          poi.sourceInteractionId ? `id:${poi.sourceInteractionId}` : '',
          normalizeText(`${poi.name}|${poi.latitude}|${poi.longitude}`),
          normalizeText(poi.name || ''),
        ])
        .flat()
        .filter(Boolean),
    );

    const stillLocal = previousDrafts.filter((poi) => {
      const keys = [
        poi.sourceInteractionId ? `id:${poi.sourceInteractionId}` : '',
        normalizeText(`${poi.name}|${poi.latitude}|${poi.longitude}`),
        normalizeText(poi.name || ''),
      ].filter(Boolean);
      const existsInRefresh = keys.some((key) => refreshedKeys.has(key));
      if (existsInRefresh) return false;
      return Boolean(poi.dirty || poi.selected || !poi.sourceInteractionId);
    });

    return [...refreshedPoiDrafts, ...stillLocal];
  };
  const editFlowStatus = useMemo(
    () => computeExperienceFlowStatus(
      {
        ...editForm,
        id: editing?.id ?? 0,
        voce: editForm.voce ?? editing?.voce,
        idPersonality: editForm.idPersonality ?? editing?.idPersonality,
        geminiPrompt: editForm.geminiPrompt,
        recoveryPrompt: editForm.recoveryPrompt,
      },
      experienceInteractions,
      { includeIntentChecks: false, includePromptCheck: false },
    ),
    [editForm, editing, experienceInteractions],
  );

  const openEditor = (experience: LegacyExperience) => {
    const experienceId = Number(experience.id || 0);
    const continuityDefaults = applyExperienceContinuityDefaults(experience);
    setIsCreating(false);
    setEditing(experience);
    setEditForm({
      name: experience.name || '',
      city: experience.city || '',
      description: experience.description || '',
      nomeChatbot: String(experience.nomeChatbot || continuityDefaults.nomeChatbot || ''),
      frase_avvio_percorso: pickInitialMessageCandidate(experience),
      messIntermedio: String(experience.messIntermedio || continuityDefaults.messIntermedio || ''),
      messFinale: String(experience.messFinale || continuityDefaults.messFinale || ''),
      isPublished: Boolean(experience.isPublished),
      duration: experience.duration,
      distance: experience.distance,
      accessibility: Boolean(experience.accessibility),
      isKids: Boolean(experience.isKids),
      isAtHome: Boolean(experience.isAtHome),
      isOnTheSite: Boolean(experience.isOnTheSite),
      latitude: experience.latitude ?? null,
      longitude: experience.longitude ?? null,
      tutorDescription: experience.tutorDescription || '',
      tutorPersonality: experience.tutorPersonality || '',
      tutorKnowledge: String(experience.tutorKnowledge || continuityDefaults.tutorKnowledge || ''),
      geminiPrompt: String(experience.geminiPrompt || ''),
      recoveryPrompt: String(experience.recoveryPrompt || ''),
      tutorLanguageRegister: experience.tutorLanguageRegister || '',
      tutorEpoch: experience.tutorEpoch || '',
      responseLength: normalizeResponseLength(experience.responseLength),
      routePlan: String(experience.routePlan || ''),
      language: experience.language || 'it',
      voce: experience.voce,
      idPersonality: experience.idPersonality,
      image: experience.image || '',
      logo: experience.logo || '',
      avatar: experience.avatar || '',
    });
    const resolvedCover =
      resolveExperienceCoverCandidates(experience)[0]
      || resolveAvatarUrl(experience.avatar)
      || '';
    setCoverPreview(resolvedCover || '');
    setCoverFile(null);
    setActiveTab('base');
    setAutoFillLocation(experience.city || experience.name || '');
    setAutoFillError(null);
    setPoiPrompt('');
    setPoiError(null);
    setPoiInfo(null);
    setPoiDrafts([]);
    setSelectedPoiMapId(null);
    setLoadedGeniusOverride({});
    if (Number.isFinite(experienceId) && experienceId > 0) {
      setSearchParams({ experienceId: String(experienceId) }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
    void (async () => {
      if (!Number.isFinite(experienceId) || experienceId <= 0) {
        return;
      }
      let resolvedExperienceDetail: LegacyExperience | null = null;
      // Il Genius Override su Firestore e la fonte AUTORITATIVA per i campi
      // estesi del personaggio. Viene caricato in parallelo al detail legacy
      // cosi il form puo mostrare subito i dati persistenti anche quando
      // il backend legacy li ha scartati.
      let overrideLoaded: ExperienceGeniusOverride | null = null;
      try {
        overrideLoaded = await getExperienceGeniusOverride(experienceId);
        setLoadedGeniusOverride(overrideLoaded || {});
      } catch (overrideErr) {
        console.warn('[ExperiencesNew] openEditor genius override fetch failed', overrideErr);
        setLoadedGeniusOverride({});
      }
      try {
        const detail = await getExperienceById(experienceId);
        resolvedExperienceDetail = detail;
        const detailDefaults = applyExperienceContinuityDefaults(detail);
        setEditing((prev) => {
          if (!prev || Number(prev.id || 0) !== experienceId) return prev;
          return {
            ...detail,
            geminiPrompt: String(prev.geminiPrompt || detail.geminiPrompt || ''),
            recoveryPrompt: String(prev.recoveryPrompt || detail.recoveryPrompt || ''),
            tutorPersonality: overrideLoaded?.tutorPersonality || overrideLoaded?.jungArchetype || prev.tutorPersonality || detail.tutorPersonality,
            voce: overrideLoaded?.voce ?? prev.voce ?? detail.voce,
            idPersonality: overrideLoaded?.idPersonality ?? prev.idPersonality ?? detail.idPersonality,
          };
        });
        setEditForm((prev) => {
          // Priorita: 1) modifiche in corso dell utente (prev)
          //           2) Firestore genius override (fonte autoritativa)
          //           3) detail legacy
          //           4) defaults di continuita
          const ov = overrideLoaded || {};
          const nextTutorDescription = String(
            prev.tutorDescription || ov.tutorDescription || detail.tutorDescription || '',
          );
          const nextTutorPersonality = String(
            prev.tutorPersonality || ov.tutorPersonality || detail.tutorPersonality || '',
          );
          const nextTutorKnowledge = String(
            prev.tutorKnowledge
              || ov.tutorKnowledge
              || detail.tutorKnowledge
              || detailDefaults.tutorKnowledge
              || '',
          );
          const nextTutorLanguageRegister = String(
            prev.tutorLanguageRegister
              || ov.tutorLanguageRegister
              || detail.tutorLanguageRegister
              || '',
          ).trim();
          const nextTutorEpoch = String(
            prev.tutorEpoch || ov.tutorEpoch || detail.tutorEpoch || '',
          ).trim();
          const nextGeminiPrompt = String(
            prev.geminiPrompt || ov.geminiPrompt || detail.geminiPrompt || '',
          ).trim();
          const nextRecoveryPrompt = String(
            prev.recoveryPrompt || ov.recoveryPrompt || detail.recoveryPrompt || '',
          ).trim();
          const nextInitialMessage = isMeaningfulInitialMessage(prev.frase_avvio_percorso, prev)
            ? String(prev.frase_avvio_percorso || '').trim()
            : pickInitialMessageCandidate(ov, detail, experience);
          const nextImage = String(prev.image || detail.image || detail.logo || '').trim();
          const nextLogo = String(prev.logo || detail.logo || detail.image || '').trim();
          const nextAvatar = String(prev.avatar || detail.avatar || '').trim();
          const nextNomeChatbot = String(
            prev.nomeChatbot
              || ov.nomeChatbot
              || detail.nomeChatbot
              || detailDefaults.nomeChatbot
              || '',
          );
          return {
            ...prev,
            duration: prev.duration ?? detail.duration,
            distance: prev.distance ?? detail.distance,
            accessibility: prev.accessibility ?? detail.accessibility ?? false,
            isKids: prev.isKids ?? detail.isKids ?? false,
            isAtHome: prev.isAtHome ?? detail.isAtHome ?? false,
            isOnTheSite: prev.isOnTheSite ?? detail.isOnTheSite ?? false,
            nomeChatbot: nextNomeChatbot,
            frase_avvio_percorso: nextInitialMessage,
            tutorDescription: nextTutorDescription,
            tutorPersonality: nextTutorPersonality,
            tutorKnowledge: nextTutorKnowledge,
            geminiPrompt: nextGeminiPrompt,
            recoveryPrompt: nextRecoveryPrompt,
            tutorLanguageRegister: nextTutorLanguageRegister,
            tutorEpoch: nextTutorEpoch,
            responseLength: normalizeResponseLength(prev.responseLength || ov.responseLength || detail.responseLength),
            routePlan: String(prev.routePlan || ov.routePlan || detail.routePlan || ''),
            language: String(prev.language || detail.language || 'it'),
            voce: ov.voce ?? prev.voce ?? detail.voce,
            idPersonality: ov.idPersonality ?? prev.idPersonality ?? detail.idPersonality,
            image: nextImage,
            logo: nextLogo,
            avatar: nextAvatar,
          };
        });
        // Se l override contiene anche prompt/voice, li propaghiamo su
        // editing cosi onSave li rispedisce al legacy E al Firestore.
        if (overrideLoaded) {
          setEditing((prev) => {
            if (!prev || Number(prev.id || 0) !== experienceId) return prev;
            return {
              ...prev,
              geminiPrompt: prev.geminiPrompt || overrideLoaded.geminiPrompt || '',
              recoveryPrompt: prev.recoveryPrompt || overrideLoaded.recoveryPrompt || '',
              tutorPersonality:
                overrideLoaded.tutorPersonality
                || overrideLoaded.jungArchetype
                || prev.tutorPersonality,
              voce: overrideLoaded.voce ?? prev.voce ?? detail.voce,
              idPersonality: overrideLoaded.idPersonality ?? prev.idPersonality ?? detail.idPersonality,
            } as LegacyExperience;
          });
          setEditForm((prev) => ({
            ...prev,
            geminiPrompt: String(prev.geminiPrompt || overrideLoaded?.geminiPrompt || ''),
            recoveryPrompt: String(prev.recoveryPrompt || overrideLoaded?.recoveryPrompt || ''),
            frase_avvio_percorso: isMeaningfulInitialMessage(prev.frase_avvio_percorso, prev)
              ? prev.frase_avvio_percorso
              : String(overrideLoaded?.welcomeMessage || ''),
            responseLength: normalizeResponseLength(prev.responseLength || overrideLoaded?.responseLength),
            routePlan: String(prev.routePlan || overrideLoaded?.routePlan || ''),
            tutorPersonality:
              overrideLoaded?.tutorPersonality
              || overrideLoaded?.jungArchetype
              || prev.tutorPersonality,
            voce: overrideLoaded?.voce ?? prev.voce ?? detail.voce,
            idPersonality: overrideLoaded?.idPersonality ?? prev.idPersonality ?? detail.idPersonality,
          }));
        }
        const resolvedDetailCover =
          resolveExperienceCoverCandidates(detail)[0]
          || resolveAvatarUrl(detail.avatar)
          || '';
        if (resolvedDetailCover) {
          setCoverPreview((prev) => prev || resolvedDetailCover);
        }
      } catch (detailError) {
        console.warn('[ExperiencesNew] openEditor detail fetch failed', detailError);
      }
      const interactions = await refreshInteractions(experienceId);
      const startIntent = interactions.find((item) => isStartLikeIntent(item)) || null;
      const initialMessageFromIntent = extractStartIntentInitialMessage(
        startIntent,
        resolvedExperienceDetail || experience,
      );
      if (initialMessageFromIntent) {
        setEditForm((prev) => (
          isMeaningfulInitialMessage(prev.frase_avvio_percorso, prev)
            ? prev
            : { ...prev, frase_avvio_percorso: initialMessageFromIntent }
        ));
      }
      const persistedPois = buildPoiDraftsFromInteractions(interactions);
      setPoiDrafts((prev) => (prev.length > 0 ? prev : persistedPois));
      setSelectedPoiMapId((prev) => prev || persistedPois[0]?.id || null);
    })();
  };

  const openCreator = () => {
    setIsCreating(true);
    setEditing({ id: 0, name: '', description: '' });
    setEditForm({ ...emptyEditState });
    setLoadedGeniusOverride({});
    setCoverPreview('');
    setCoverFile(null);
    setActiveTab('base');
    setSaveFeedback(null);
    setAutoFillLocation('');
    setAutoFillError(null);
    setPoiPrompt('');
    setPoiError(null);
    setPoiInfo(null);
    setPoiDrafts([]);
    setSelectedPoiMapId(null);
    setExperienceInteractions([]);
    setSearchParams({}, { replace: true });
  };

  const closeEditorModal = () => {
    setEditing(null);
    setLoadedGeniusOverride({});
    setSaveFeedback(null);
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    if (loading || editing || !requestedExperienceIdSafe) return;
    const target = experiences.find((item) => item.id === requestedExperienceIdSafe);
    if (target) {
      openEditor(target);
    }
  }, [loading, editing, experiences, requestedExperienceIdSafe]);

  const pickImageFileFromList = (files: FileList | null): File | null => {
    if (!files?.length) return null;
    return Array.from(files).find((f) => f.type.startsWith('image/')) ?? null;
  };

  const onCoverChange = (file: File) => {
    setCoverPreview((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* noop */
        }
      }
      return URL.createObjectURL(file);
    });
    setCoverFile(file);
  };

  const handleCoverDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setCoverDropActive(true);
    }
  };

  const handleCoverDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setCoverDropActive(false);
  };

  const handleCoverDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setCoverDropActive(false);
    const file = pickImageFileFromList(e.dataTransfer.files);
    if (file) onCoverChange(file);
  };

  const onAutoFillWithIa = async () => {
    if (!editing || !isCreating) return;

    const location = autoFillLocation.trim() || String(editForm.city || '').trim() || String(editForm.name || '').trim();
    if (!location) {
      setAutoFillError('Inserisci un luogo geografico (es. Agropoli, Salerno) prima di avviare la compilazione automatica.');
      return;
    }

    try {
      setAutoFilling(true);
      setAutoFillError(null);

      const { experience: generatedExperience, genius: generatedGenius } = await autofillExperienceFields(location, {
        experienceName: String(editForm.name || '').trim(),
        city: String(editForm.city || '').trim(),
        description: String(editForm.description || '').trim(),
        characterName: String(editForm.nomeChatbot || '').trim(),
      });

      setEditForm((prev) => {
        const nextName = generatedExperience.name || prev.name;
        const nextCity = generatedExperience.city || prev.city;
        const nextDescription = generatedExperience.description || prev.description;
        const nextCharacterName = generatedExperience.nomeChatbot || generatedGenius.name || prev.nomeChatbot;
        const nextTutorDescription = generatedGenius.bio || prev.tutorDescription;
        const nextTutorPersonality = generatedGenius.personality || prev.tutorPersonality;
        const nextTutorKnowledge = generatedGenius.specialties?.filter(Boolean).join(', ') || prev.tutorKnowledge;
        const nextTutorLanguageRegister =
          generatedExperience.tutorLanguageRegister
          || generatedGenius.languageRegister
          || prev.tutorLanguageRegister;
        const nextTutorEpoch =
          generatedExperience.tutorEpoch
          || generatedGenius.historicalEpoch
          || prev.tutorEpoch;
        const nextTutorVoiceCode =
          String(generatedExperience.tutorVoiceCode || '').trim()
          || String(generatedGenius.voiceCode || '').trim()
          || extractVoiceCodeFromPrompt(String(prev.geminiPrompt || prev.recoveryPrompt || ''));
        const autoPrompt = buildAutoSystemPromptFromAutofill({
          characterName: nextCharacterName,
          roleOrStory: nextTutorDescription,
          tone: nextTutorLanguageRegister,
          place: nextCity || location,
          experienceName: nextName,
          experienceDescription: nextDescription,
          historicalEpoch: nextTutorEpoch,
          archetype: nextTutorPersonality,
          knowledge: nextTutorKnowledge,
          voiceCode: nextTutorVoiceCode,
        });

        return {
          ...prev,
          name: nextName,
          city: nextCity,
          description: nextDescription,
          nomeChatbot: nextCharacterName,
          frase_avvio_percorso:
            generatedExperience.frase_avvio_percorso
            || prev.frase_avvio_percorso,
          messIntermedio: generatedExperience.messIntermedio || prev.messIntermedio,
          messFinale: generatedExperience.messFinale || prev.messFinale,
          latitude:
            Number.isFinite(Number(generatedExperience.latitude))
              ? Number(generatedExperience.latitude)
              : prev.latitude,
          longitude:
            Number.isFinite(Number(generatedExperience.longitude))
              ? Number(generatedExperience.longitude)
              : prev.longitude,
          tutorDescription: nextTutorDescription,
          tutorPersonality: nextTutorPersonality,
          tutorKnowledge: nextTutorKnowledge,
          tutorLanguageRegister: nextTutorLanguageRegister,
          tutorEpoch: nextTutorEpoch,
          geminiPrompt: autoPrompt,
          recoveryPrompt: autoPrompt,
        };
      });
    } catch (err) {
      console.error(err);
      setAutoFillError('Compilazione automatica non riuscita. Verifica la connessione o riprova tra qualche secondo.');
    } finally {
      setAutoFilling(false);
    }
  };

  const onGeneratePois = async () => {
    if (!hasPersistedExperienceId) {
      setPoiError('Salva prima la nuova esperienza, poi riaprila per usare il modulo POI.');
      return;
    }
    const experienceName = String(editForm.name || '').trim();
    if (!experienceName) {
      setPoiError('Inserisci prima il nome esperienza: serve come contesto per la generazione POI.');
      return;
    }

    try {
      setPoiGenerating(true);
      setPoiError(null);
      setPoiInfo(null);

      const result = await generateExperiencePois({
        experienceId: hasPersistedExperienceId ? persistedExperienceId : undefined,
        experienceName,
        city: String(editForm.city || '').trim(),
        description: String(editForm.description || '').trim(),
        latitude: editForm.latitude ?? null,
        longitude: editForm.longitude ?? null,
        prompt: [
          poiPrompt.trim(),
          String(editForm.routePlan || '').trim()
            ? `Percorso configurato:\n${String(editForm.routePlan || '').trim()}`
            : '',
        ].filter(Boolean).join('\n\n'),
        maxPois: 8,
      });

      const drafts: PoiDraft[] = result.pois.map((poi, idx) => {
        const latitude = toFiniteNumberOrNull(poi.latitude);
        const longitude = toFiniteNumberOrNull(poi.longitude);
        const hasValidCoordinates = latitude != null
          && longitude != null
          && !hasInvalidPoiCoordinates(latitude, longitude);
        return {
          ...poi,
          id: poi.id || `poi_${Date.now().toString(36)}_${idx + 1}`,
          latitude,
          longitude,
          selected: hasValidCoordinates,
          dirty: true,
          suggestedQueries: Array.isArray(poi.suggestedQueries)
            ? poi.suggestedQueries.filter(Boolean).slice(0, 6)
            : [],
        };
      });
      const validDraftsCount = drafts.filter((poi) => poi.selected).length;
      const invalidDraftsCount = drafts.length - validDraftsCount;
      const merged = mergeGeneratedPoiDrafts(poiDrafts, drafts);
      const firstAddedDraft = merged.drafts.find((poi) => !poiDrafts.some((existing) => existing.id === poi.id));

      setPoiDrafts(merged.drafts);
      setSelectedPoiMapId((prev) => (
        prev && merged.drafts.some((poi) => poi.id === prev)
          ? prev
          : (firstAddedDraft || merged.drafts[0])?.id || null
      ));
      setPoiInfo(
        drafts.length > 0
          ? `${drafts.length} POI generati con Gemini + grounding Google Search/Maps. Aggiunti ${merged.added}, duplicati evitati ${merged.skipped}. ${validDraftsCount} hanno coordinate valide${invalidDraftsCount > 0 ? `; ${invalidDraftsCount} da correggere sulla mappa.` : '.'} I POI già presenti non sono stati sovrascritti.`
          : 'Nessun POI rilevato dal grounding. Prova un prompt più specifico.',
      );
    } catch (err) {
      console.error(err);
      setPoiError('Generazione POI non riuscita. Controlla log Functions e configurazione Gemini.');
    } finally {
      setPoiGenerating(false);
    }
  };

  const onCreatePoiInteractions = async (targetDrafts?: PoiDraft[], experienceIdOverride?: number): Promise<{
    created: number;
    persisted: number;
    failed: number;
  } | null> => {
    const effectiveExperienceId = Number(experienceIdOverride || persistedExperienceId || 0);
    const experienceId = Number.isFinite(effectiveExperienceId) && effectiveExperienceId > 0
      ? effectiveExperienceId
      : null;
    const experienceLabel = String(editForm.name || '').trim() || 'esperienza';
    if (!experienceId) {
      setPoiError('Salva prima l\'esperienza e riapri il modulo: serve un ID esperienza per creare POI/interazioni.');
      return null;
    }

    const selected = targetDrafts && targetDrafts.length > 0
      ? targetDrafts.map((item) => ({ ...item, selected: true }))
      : poiDrafts.filter((item) => item.selected || item.dirty);
    if (selected.length === 0) {
      setPoiError('Apri un POI, scegli la posizione sulla mappa e premi Salva POI.');
      return null;
    }

    setPoiSaving(true);
    setPoiError(null);
    setPoiInfo(null);

    try {
      console.info('[POI_DEBUG] start create intents', {
        experienceId,
        experienceLabel,
        totalDrafts: poiDrafts.length,
        selectedDrafts: selected.length,
      });
      const previousDrafts = poiDrafts;
      const currentInteractions = experienceInteractions.length > 0
        ? experienceInteractions
        : await getInteractionsByExperience({ idExperience: experienceId });

      const existingNames = new Set(
        currentInteractions
          .map((interaction) => normalizeText(interaction.displayName || ''))
          .filter(Boolean),
      );
      const existingActions = new Set(
        currentInteractions
          .map((interaction) => normalizeText(interaction.action || ''))
          .filter(Boolean),
      );

      let created = 0;
      let updated = 0;
      let skipped = 0;
      let missingCoords = 0;
      let invalidCoords = 0;
      let failed = 0;
      const createdDisplayNames: string[] = [];
      const failedDisplayNames: string[] = [];
      const invalidCoordDisplayNames: string[] = [];

      for (const poi of selected) {
        const displayName = sanitizePoiText(poi.name, POI_LIMITS.displayName);
        if (!displayName) {
          console.warn('[POI_DEBUG] skip poi without displayName', { rawPoi: poi });
          skipped += 1;
          continue;
        }
        const latitude = Number(poi.latitude);
        const longitude = Number(poi.longitude);
        if (hasInvalidPoiCoordinates(latitude, longitude)) {
          invalidCoords += 1;
          invalidCoordDisplayNames.push(displayName || String(poi.name || 'POI senza nome'));
          console.warn('[POI_DEBUG] skip poi missing coordinates', {
            displayName,
            latitude: poi.latitude,
            longitude: poi.longitude,
          });
          missingCoords += 1;
          continue;
        }
        const quickReplies = dedupeArray((poi.suggestedQueries || []).map((value) => sanitizePoiText(value, POI_LIMITS.quickReply)))
          .slice(0, 6);
        const summary = sanitizePoiText(poi.summary, POI_LIMITS.message);
        const address = sanitizePoiText(poi.address, POI_LIMITS.description);
        const messageText = summary || address || sanitizePoiText(`Ecco informazioni su ${displayName}.`, POI_LIMITS.message);
        const description = sanitizePoiText(
          poi.address || poi.summary || `POI IA per ${experienceLabel}`,
          POI_LIMITS.description,
        );
        const mapsUrl = sanitizePoiText(poi.mapsUrl, POI_LIMITS.mediaLink);

        const matchedExistingInteraction = currentInteractions.find((interaction) => {
          const sameSourceId = poi.sourceInteractionId
            && Number(interaction.id || 0) === Number(poi.sourceInteractionId);
          const sameDialogflowId = poi.sourceDialogflowIntentId
            && interaction.dialogflowIntentId === poi.sourceDialogflowIntentId;
          const sameAction = poi.sourceAction
            && normalizeText(String(interaction.action || '')) === normalizeText(String(poi.sourceAction || ''));
          const sameDisplayName =
            normalizeText(interaction.displayName || '') === normalizeText(displayName);
          return Boolean(sameSourceId || sameDialogflowId || sameAction || sameDisplayName);
        });

        if (!matchedExistingInteraction && existingNames.has(normalizeText(displayName))) {
          console.info('[POI_DEBUG] skip duplicated poi by displayName', { displayName });
          skipped += 1;
          continue;
        }

        const action = sanitizePoiText(
          matchedExistingInteraction?.action || poi.sourceAction || '',
          POI_LIMITS.action,
        ) || buildUniquePoiAction(experienceId, displayName, existingActions);
        const poiTrainingPhrases = buildPoiTrainingPhrases(displayName, quickReplies);
        const poiMessages = buildPoiMessagePayload({
          messageText,
          quickReplies,
          mapsUrl,
          displayName,
        });
        console.info('[POI_DEBUG] creating poi intent', {
          experienceId,
          displayName,
          action,
          latitude,
          longitude,
          hasQuickReplies: quickReplies.length > 0,
          hasMapsUrl: Boolean(mapsUrl),
          mode: matchedExistingInteraction ? 'update' : 'create',
        });

        if (matchedExistingInteraction) {
          try {
            const updatedInteraction = await updateInteraction({
              ...matchedExistingInteraction,
              idExperience: experienceId,
              displayName,
              description,
              action,
              latitude,
              longitude,
              mlDisabled: true,
              resetContexts: false,
              isFallback: false,
              genericInteraction: false,
              trainingPhraseDTO: poiTrainingPhrases,
              inputContextNamesDTO: matchedExistingInteraction.inputContextNamesDTO || [],
              outputContextsDTO: matchedExistingInteraction.outputContextsDTO || [],
              messagesDTO: poiMessages,
              eventsDTO: matchedExistingInteraction.eventsDTO || [],
              recoveryPrompt: '',
              promptRecupero: '',
              promptDiRecupero: '',
              geminiPrompt: '',
            });
            const savedAction = updatedInteraction.action || action;
            console.info('[POI_DEBUG] updateInteraction success', { displayName, action: savedAction });
            updated += 1;
            createdDisplayNames.push(displayName);
            existingNames.add(normalizeText(displayName));
            existingActions.add(normalizeText(savedAction));
            continue;
          } catch (updateError) {
            console.warn('[POI_DEBUG] updateInteraction failed, trying create fallback', {
              displayName,
              action,
              updateError,
            });
          }
        }

        const basePayload: Omit<LegacyInteraction, 'id'> = {
          idExperience: experienceId,
          displayName,
          description,
          action,
          // Per compatibilita' con backend legacy: lasciamo i prompt vuoti sui POI.
          // Gli intent POI usano il testo in messagesDTO, come la creazione manuale intent.
          recoveryPrompt: '',
          promptRecupero: '',
          promptDiRecupero: '',
          geminiPrompt: '',
          mlDisabled: true,
          resetContexts: false,
          isFallback: false,
          genericInteraction: false,
          latitude,
          longitude,
          // Mantieni trigger testuale coerente con quick reply/click POI in mobile.
          trainingPhraseDTO: poiTrainingPhrases,
          inputContextNamesDTO: [],
          outputContextsDTO: [],
          messagesDTO: poiMessages,
          eventsDTO: [],
        };

        let savedAction = action;
        try {
          await createInteraction(basePayload);
          console.info('[POI_DEBUG] createInteraction success', { displayName, action: savedAction });
        } catch (primaryError) {
          console.warn('[POI_DEBUG] createInteraction primary failed, trying fallback', {
            displayName,
            action,
            primaryError,
          });
          try {
            const fallbackAction = buildUniquePoiAction(experienceId, `${displayName}.${Date.now()}`, existingActions);
            await createInteraction({
              ...basePayload,
              action: fallbackAction,
              messagesDTO: buildPoiMessagePayload({
                messageText,
                quickReplies: [],
                mapsUrl: '',
                displayName,
              }),
            });
            savedAction = fallbackAction;
            console.info('[POI_DEBUG] createInteraction fallback success', {
              displayName,
              action: savedAction,
            });
          } catch (secondaryError) {
            console.warn('[POI_DEBUG] createInteraction fallback failed, trying emergency minimal create', {
              experienceId,
              displayName,
              action,
              primaryError,
              secondaryError,
            });
            try {
              const emergencyAction = buildUniquePoiAction(experienceId, `${displayName}-emergency`, existingActions);
              const emergencyCreated = await createInteraction({
                ...basePayload,
                action: emergencyAction,
                latitude: null,
                longitude: null,
                description: sanitizePoiText(displayName, POI_LIMITS.description),
                messagesDTO: [{
                  textStringDTOList: [{ text: sanitizePoiText(messageText, POI_LIMITS.message) || displayName }],
                }],
              });
              savedAction = emergencyCreated.action || emergencyAction;
              try {
                const updatedEmergency = await updateInteraction({
                  ...emergencyCreated,
                  idExperience: experienceId,
                  displayName,
                  description,
                  action: savedAction,
                  latitude,
                  longitude,
                  mlDisabled: true,
                  trainingPhraseDTO: [],
                  messagesDTO: poiMessages,
                  eventsDTO: [],
                  recoveryPrompt: '',
                  promptRecupero: '',
                  promptDiRecupero: '',
                  geminiPrompt: '',
                });
                savedAction = updatedEmergency.action || savedAction;
              } catch (postCreateUpdateError) {
                console.warn('[POI_DEBUG] emergency create succeeded but update failed', {
                  displayName,
                  action: savedAction,
                  postCreateUpdateError,
                });
              }
              console.info('[POI_DEBUG] emergency createInteraction success', {
                displayName,
                action: savedAction,
              });
            } catch (emergencyError) {
              console.warn('[ExperiencesNew] create POI interaction failed', {
                experienceId,
                displayName,
                primaryError,
                secondaryError,
                emergencyError,
              });
              failed += 1;
              failedDisplayNames.push(displayName);
              continue;
            }
          }
        }

        created += 1;
        createdDisplayNames.push(displayName);
        existingNames.add(normalizeText(displayName));
        existingActions.add(normalizeText(savedAction));
      }

      const refreshedInteractions = await refreshInteractions(experienceId);
      const refreshedPoiDrafts = buildPoiDraftsFromInteractions(refreshedInteractions);
      const mergedDrafts = mergePoiDraftsAfterRefresh(refreshedPoiDrafts, previousDrafts);
      setPoiDrafts(mergedDrafts);
      setSelectedPoiMapId((prev) => {
        if (prev && mergedDrafts.some((item) => item.id === prev)) return prev;
        return mergedDrafts[0]?.id || null;
      });
      const refreshedNames = new Set(
        refreshedInteractions
          .map((interaction) => normalizeText(interaction.displayName || ''))
          .filter(Boolean),
      );
      const persistedByLookup = createdDisplayNames.filter((name) => refreshedNames.has(normalizeText(name))).length;
      const attemptedWrites = created + updated;
      const persisted = Math.max(persistedByLookup, Math.max(0, attemptedWrites - failed));
      console.info('[POI_DEBUG] post-refresh persistence check', {
        created,
        updated,
        persistedByLookup,
        persisted,
        skipped,
        missingCoords,
        invalidCoords,
        invalidCoordDisplayNames,
        failed,
        createdDisplayNames,
        failedDisplayNames,
      });

      if (persisted < attemptedWrites) {
        failed = attemptedWrites - persisted;
      }
      setPoiInfo(
        `POI creati: ${created}. Aggiornati: ${updated}. Persistiti: ${persisted}. Duplicati saltati: ${skipped}. Senza coordinate: ${missingCoords}.`,
      );
      if (invalidCoords > 0) {
        const previewInvalid = invalidCoordDisplayNames.slice(0, 3).join(', ');
        const suffixInvalid = invalidCoordDisplayNames.length > 3 ? ', ...' : '';
        setPoiError(
          `${invalidCoords} POI hanno coordinate non valide (spesso 0,0). Correggi lat/lng nel pannello mappa e riprova.${previewInvalid ? ` Esempi: ${previewInvalid}${suffixInvalid}.` : ''}`,
        );
      }
      if (failed > 0) {
        console.table(
          failedDisplayNames.map((name) => ({
            experienceId,
            poiName: name,
            reason: 'createInteraction failed or not persisted after refresh',
          })),
        );
        const preview = failedDisplayNames.slice(0, 3).join(', ');
        const suffix = failedDisplayNames.length > 3 ? ', ...' : '';
        setPoiError(
          `${failed} POI non risultano persistiti lato backend.${preview ? ` Falliti: ${preview}${suffix}.` : ''} Riprova con "Salva POI".`,
        );
      }
      return { created, persisted, failed };
    } catch (err) {
      console.error(err);
      setPoiError('Creazione POI non riuscita. Verifica payload interazioni e permessi API legacy.');
      return null;
    } finally {
      setPoiSaving(false);
    }
  };

  const onSaveCurrentPoi = async () => {
    if (!selectedPoi) {
      setPoiError('Seleziona un POI dall’elenco prima di salvarlo.');
      return;
    }

    const result = await onCreatePoiInteractions([{ ...selectedPoi, selected: true }]);
    if (result && result.failed === 0 && result.persisted > 0) {
      setPoiInfo(`POI salvato: ${selectedPoi.name || 'POI'}.`);
    }
  };

  const onSave = async (forcedPublishedState?: boolean) => {
    if (!editing) return;
    if (!editForm.name?.trim()) {
      setError('Il nome dell\'esperienza è obbligatorio.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSaveFeedback(null);

      let imageUrl = (editForm.image as string) || '';
      if (coverFile) {
        setCoverUploading(true);
        try {
          const coverEntityId = isCreating
            ? `n${Date.now().toString(36)}`
            : editing.id;
          const uploaded = await uploadExperienceCover(coverFile, coverEntityId);
          imageUrl = uploaded.storedValue;
          setEditForm((p) => ({ ...p, image: imageUrl }));
          setCoverPreview(uploaded.downloadUrl);
          setCoverFile(null);
        } finally {
          setCoverUploading(false);
        }
      }

      const continuityDefaults = isCreating
        ? applyExperienceContinuityDefaults(editForm)
        : {};
      const payload: LegacyExperience = {
        ...editing,
        ...editForm,
        ...continuityDefaults,
        isPublished: forcedPublishedState ?? Boolean(editForm.isPublished),
        frase_avvio_percorso: resolveInitialMessageText(editForm),
        geminiPrompt: String(
          editForm.geminiPrompt || editing?.geminiPrompt || loadedGeniusOverride.geminiPrompt || '',
        ).trim(),
        recoveryPrompt: String(
          editForm.recoveryPrompt || editing?.recoveryPrompt || loadedGeniusOverride.recoveryPrompt || '',
        ).trim(),
        image: imageUrl,
        logo: imageUrl || (editForm.logo as string) || '',
      };
      const initialMessage = resolveInitialMessageText(payload);
      const avatarRaw = String(payload.avatar || '').trim();
      const coverRaw = String(payload.image || payload.logo || '').trim();
      const previousCoverSources = [
        String(editing.image || '').trim(),
        String(editing.logo || '').trim(),
      ].filter(Boolean);
      const previousCoverCandidates = resolveExperienceCoverCandidates({
        id: editing.id,
        image: String(editing.image || '').trim(),
        logo: String(editing.logo || '').trim(),
      });
      const normalizedCover = normalizeComparableAssetValue(coverRaw);
      const normalizedPreviousCover = normalizeComparableAssetValue(
        String(editing.image || editing.logo || '').trim(),
      );
      const hasCoverChanged = Boolean(normalizedCover)
        && normalizedCover !== normalizedPreviousCover;
      if (coverRaw && (
        hasCoverChanged
        || shouldSyncAvatarWithCover({
          avatar: avatarRaw,
          previousCoverSources,
          previousCoverCandidates,
        })
      )) {
        payload.avatar = coverRaw;
      }
      (payload as LegacyExperience & { img?: string }).img = coverRaw;

      // Limiti conservativi del DB legacy (sincronizzati con EXPERIENCE_LIMITS in legacyApiService).
      // NB: geminiPrompt/recoveryPrompt ridotti a 2900 perche' con 3504 il backend
      // restituiva DataException (varchar overflow su colonna 3000).
      const LEGACY_LIMITS: Record<string, number> = {
        description: 700, frase_avvio_percorso: 250, messIntermedio: 250, messFinale: 250,
        nomeChatbot: 45, tutorKnowledge: 255, tutorDescription: 255,
        tutorPersonality: 250, geminiPrompt: 2900, recoveryPrompt: 2900,
        tutorLanguageRegister: 255, tutorEpoch: 255,
      };
      const truncatedFields: string[] = [];
      Object.entries(LEGACY_LIMITS).forEach(([field, limit]) => {
        const val = String(((payload as unknown) as Record<string, unknown>)[field] || '');
        if (val.length > limit) truncatedFields.push(`${field}(${val.length}→${limit})`);
      });
      if (truncatedFields.length > 0 && EXP_SAVE_DEBUG) {
        console.warn('[EXP_SAVE] campi che verranno troncati dal DB legacy:', truncatedFields.join(', '));
      }

      // Log come stringa tabellare
      if (EXP_SAVE_DEBUG) {
        console.info(
          '[EXP_SAVE] payload field lengths | ' +
          `name=${String(payload.name||'').length} | description=${String(payload.description||'').length} | ` +
          `city=${String(payload.city||'').length} | nomeChatbot=${String(payload.nomeChatbot||'').length} | ` +
          `image=${String(payload.image||'').length}(val="${imageUrl}") | avatar=${String(payload.avatar||'').length} | ` +
          `frase_avvio_percorso=${String(payload.frase_avvio_percorso||'').length} | messIntermedio=${String(payload.messIntermedio||'').length} | messFinale=${String(payload.messFinale||'').length} | ` +
          `tutorDescription=${String(payload.tutorDescription||'').length} | tutorPersonality=${String(payload.tutorPersonality||'').length} | ` +
          `tutorKnowledge=${String(payload.tutorKnowledge||'').length} | geminiPrompt=${String(payload.geminiPrompt||'').length} | ` +
          `recoveryPrompt=${String(payload.recoveryPrompt||'').length}`,
        );
      }

      let updated: LegacyExperience;
      let persistedExperienceId = Number(editing.id || 0);
      if (isCreating) {
        const { id: _ignoredId, ...createPayload } = payload;
        updated = await createExperience(createPayload as Omit<LegacyExperience, 'id'>);
        persistedExperienceId = Number(updated.id || payload.id || 0);
        const createdId = Number(updated.id || payload.id || 0);
        if (Number.isFinite(createdId) && createdId > 0) {
          try {
            // Alcuni backend legacy ignorano parte dei campi custom in POST create:
            // forziamo una PUT immediata per persistere campi Genius/APE.
            const reinforcedPayload: LegacyExperience = {
              ...payload,
              ...updated,
              id: createdId,
              tutorDescription: String(
                payload.tutorDescription || updated.tutorDescription || '',
              ).trim(),
              tutorPersonality: String(
                payload.tutorPersonality || updated.tutorPersonality || '',
              ).trim(),
              tutorLanguageRegister: String(
                payload.tutorLanguageRegister || updated.tutorLanguageRegister || '',
              ).trim(),
              tutorEpoch: String(
                payload.tutorEpoch || updated.tutorEpoch || '',
              ).trim(),
            };
            updated = await updateExperience(reinforcedPayload, {
              quiet: true,
              maxDataExceptionRetries: 0,
            });
          } catch (reinforceError) {
            if (EXP_SAVE_DEBUG) {
              console.warn(
                '[EXP_SAVE] create reinforcement update failed; continuing with create response',
                reinforceError,
              );
            }
          }
        }
        const normalizedCover = String(imageUrl || updated.image || updated.logo || '').trim();
        const targetExperienceId = Number(updated.id || payload.id || 0);
        if (normalizedCover && Number.isFinite(targetExperienceId) && targetExperienceId > 0) {
          setExperienceCoverOverride(targetExperienceId, normalizedCover);
        }
        const mergedCreated: LegacyExperience = {
          ...updated,
          ...payload,
          id: createdId,
          image: normalizedCover || updated.image || updated.logo || '',
          logo: normalizedCover || updated.logo || updated.image || '',
        };
        setExperiences((prev) => applyExperienceCoverOverrides([mergedCreated, ...prev]));
      } else {
        if (imageUrl && Number(editing.id || 0) > 0) {
          // Persist local override immediately using the known editing id.
          // This avoids regressions when legacy PUT response omits/changes id.
          setExperienceCoverOverride(editing.id, imageUrl);
        }
        updated = await updateExperience(payload, {
          quiet: true,
          maxDataExceptionRetries: 1,
        });
        persistedExperienceId = Number(updated.id || payload.id || editing.id || 0);
        const normalizedCover = String(imageUrl || payload.image || updated.image || updated.logo || '').trim();
        const targetExperienceId = Number(updated.id || payload.id || editing.id || 0);
        if (normalizedCover && Number.isFinite(targetExperienceId) && targetExperienceId > 0) {
          setExperienceCoverOverride(targetExperienceId, normalizedCover);
        }
        if (!normalizedCover && Number.isFinite(targetExperienceId) && targetExperienceId > 0) {
          setExperienceCoverOverride(targetExperienceId, null);
        }
        setExperiences((prev) => applyExperienceCoverOverrides(
          prev.map((item) => {
            if (item.id !== targetExperienceId) return item;
            return {
              ...item,
              ...updated,
              ...payload,
              image: normalizedCover || item.image || '',
              logo: normalizedCover || item.logo || '',
            };
          }),
        ));
      }

      // Persisti SEMPRE l override Genius su Firestore per i campi che il
      // backend legacy scarta/tronca (DataException). Questa e la fonte
      // autoritativa letta dall openEditor e dal motore dialogflowDetectIntent.
      if (Number.isFinite(persistedExperienceId) && persistedExperienceId > 0) {
        try {
          const overridePayload: ExperienceGeniusOverride = {
            tutorDescription: String(payload.tutorDescription || '').trim(),
            tutorPersonality: String(payload.tutorPersonality || '').trim(),
            jungArchetype: String(payload.tutorPersonality || '').trim(),
            tutorKnowledge: String(payload.tutorKnowledge || '').trim(),
            tutorLanguageRegister: String(payload.tutorLanguageRegister || '').trim(),
            tutorEpoch: String(payload.tutorEpoch || '').trim(),
            geminiPrompt: String(
              payload.geminiPrompt || loadedGeniusOverride.geminiPrompt || '',
            ).trim(),
            recoveryPrompt: String(
              payload.recoveryPrompt || loadedGeniusOverride.recoveryPrompt || '',
            ).trim(),
            voce: Number.isFinite(Number(payload.voce)) ? Number(payload.voce) : undefined,
            idPersonality: Number.isFinite(Number(payload.idPersonality)) ? Number(payload.idPersonality) : undefined,
            voiceCode: loadedGeniusOverride.voiceCode,
            voiceName: loadedGeniusOverride.voiceName,
            nomeChatbot: String(payload.nomeChatbot || '').trim(),
            welcomeMessage: initialMessage,
            responseLength: normalizeResponseLength(payload.responseLength),
            routePlan: String(payload.routePlan || '').trim(),
          };
          await saveExperienceGeniusOverride(persistedExperienceId, overridePayload);
        } catch (overrideErr) {
          console.warn('[ExperiencesNew] genius override save failed', overrideErr);
        }
      }

      if (Number.isFinite(persistedExperienceId) && persistedExperienceId > 0 && initialMessage) {
        try {
          const interactionsForExperience = await getInteractionsByExperience({ idExperience: persistedExperienceId });
          const startIntent = interactionsForExperience.find((item) => isStartLikeIntent(item)) || null;
          const ensuredMessagesDTO = (() => {
            const sourceMessages = Array.isArray(startIntent?.messagesDTO) && startIntent.messagesDTO.length > 0
              ? startIntent.messagesDTO
              : [{ textStringDTOList: [] }];
            const first = sourceMessages[0] || { textStringDTOList: [] };
            const textRows = Array.isArray(first.textStringDTOList) ? [...first.textStringDTOList] : [];
            if (textRows.length === 0) textRows.push({ text: initialMessage });
            else textRows[0] = { ...textRows[0], text: initialMessage };
            return [{
              ...first,
              textStringDTOList: textRows,
            }];
          })();

          if (startIntent?.id) {
            await updateInteraction({
              ...startIntent,
              id: startIntent.id,
              idExperience: persistedExperienceId,
              description: initialMessage,
              action: String(startIntent.action || START_INTENT_ACTION).trim() || START_INTENT_ACTION,
              isFallback: false,
              eventsDTO: Array.isArray(startIntent.eventsDTO) && startIntent.eventsDTO.length > 0
                ? startIntent.eventsDTO
                : START_EVENT_NAMES.map((name) => ({ name })),
              messagesDTO: ensuredMessagesDTO,
            } as LegacyInteraction);
          } else {
            await createInteraction({
              idExperience: persistedExperienceId,
              displayName: toDialogflowDisplayNameSafe(
                `${String(payload.name || 'welcome').trim()} _exp-${persistedExperienceId}_start`,
              ),
              description: initialMessage,
              action: START_INTENT_ACTION,
              isFallback: false,
              mlDisabled: false,
              resetContexts: false,
              genericInteraction: false,
              latitude: null,
              longitude: null,
              trainingPhraseDTO: [{ type: '1', partsListDTO: [{ text: 'start' }] }],
              inputContextNamesDTO: [],
              outputContextsDTO: [],
              eventsDTO: START_EVENT_NAMES.map((name) => ({ name })),
              messagesDTO: ensuredMessagesDTO,
              recoveryPrompt: String(payload.recoveryPrompt || '').trim(),
              promptRecupero: String(payload.recoveryPrompt || '').trim(),
              promptDiRecupero: String(payload.recoveryPrompt || '').trim(),
              geminiPrompt: String(payload.geminiPrompt || '').trim(),
            });
          }
        } catch (startSyncError) {
          console.warn('[ExperiencesNew] sync start intent failed', {
            experienceId: persistedExperienceId,
            startSyncError,
          });
        }
      }

      const pendingPoiDrafts = poiDrafts.filter((item) => item.selected || item.dirty);
      let poiSaveFeedback = '';
      if (pendingPoiDrafts.length > 0 && Number.isFinite(persistedExperienceId) && persistedExperienceId > 0) {
        const poiResult = await onCreatePoiInteractions(pendingPoiDrafts, persistedExperienceId);
        if (!poiResult) {
          setActiveTab('poi');
          poiSaveFeedback = ' I POI non sono stati salvati: controlla il tab POI AI Mappa.';
        } else if (poiResult.failed > 0 || poiResult.persisted < pendingPoiDrafts.length) {
          setActiveTab('poi');
          poiSaveFeedback = ` POI salvati: ${poiResult.persisted}/${pendingPoiDrafts.length}. Controlla quelli rimasti nel tab POI AI Mappa.`;
        } else {
          poiSaveFeedback = ` Salvati anche ${poiResult.persisted} POI.`;
        }
      }

      const savedExperienceId = Number(
        persistedExperienceId || updated.id || payload.id || editing.id || 0,
      );
      const normalizedSavedCover = String(
        payload.image || payload.logo || updated.image || updated.logo || '',
      ).trim();
      const savedExperience: LegacyExperience = applyExperienceCoverOverrides([{
        ...editing,
        ...updated,
        ...payload,
        id: savedExperienceId,
        image: normalizedSavedCover || updated.image || payload.image || '',
        logo: normalizedSavedCover || updated.logo || payload.logo || '',
      }])[0];

      setEditing(savedExperience);
      setIsCreating(false);
      const { id: _savedId, ...savedEditForm } = savedExperience;
      setEditForm((prev) => ({
        ...prev,
        ...savedEditForm,
        isPublished: Boolean(savedExperience.isPublished),
        image: savedExperience.image || '',
        logo: savedExperience.logo || '',
      }));
      if (Number.isFinite(savedExperienceId) && savedExperienceId > 0) {
        setSearchParams({ experienceId: String(savedExperienceId) }, { replace: true });
      }
      setSaveFeedback(
        `${isCreating ? 'Esperienza creata' : 'Esperienza salvata'} correttamente.${poiSaveFeedback} Puoi continuare a modificarla oppure tornare alla lista esperienze.`,
      );
    } catch (err) {
      console.error('[EXP_SAVE] error:', err);
      const rawMessage = err instanceof Error ? err.message : String(err || '');
      const errStatus = (err as { status?: number } | null)?.status;
      console.error('[EXP_SAVE] status:', errStatus, 'message:', rawMessage);
      if (rawMessage.includes('DataException') || rawMessage.includes('could not execute statement')) {
        setError('Errore nel salvataggio: uno o piu campi superano i limiti legacy (in particolare conoscenza/prompt Genius Loci). Riduci i testi e riprova.');
      } else if (Number(errStatus) >= 500) {
        setError(`Errore ${errStatus} dal server. Controlla console per i dettagli sui campi (lunghezze). Prova a ridurre descrizione, bio o prompt.`);
      } else {
        setError('Errore nel salvataggio. Riprova o controlla i campi obbligatori.');
      }
    } finally {
      setSaving(false);
    }
  };

  const onDeleteSingleExperience = async (experience: LegacyExperience) => {
    const experienceId = Number(experience.id || 0);
    if (!Number.isFinite(experienceId) || experienceId <= 0) return;

    const confirmed = window.confirm(
      `Rimuovere "${experience.name}" da app e admin?\n\n` +
      'L\'esperienza verrà rimossa dalla webapp tramite hide globale e verranno eliminati gli intent Dialogflow associati. ' +
      'Il record legacy non verrà cancellato via bot-domains.',
    );
    if (!confirmed) return;

    try {
      setDeletingExperienceId(experienceId);
      setError(null);
      setPoiError(null);
      setPoiInfo(null);

      const result = await hideExperience(experienceId, {
        experienceName: experience.name || '',
        reason: 'admin_manual_hide',
        legacyOutcome: 'legacy_delete_skipped',
      });

      setExperiences((prev) => prev.filter((item) => item.id !== experienceId));
      setExperienceInteractions([]);
      setPoiDrafts([]);
      setSelectedPoiMapId(null);
      setSearchParams({}, { replace: true });
      if (editing?.id === experienceId) {
        closeEditorModal();
      }
      setPoiInfo(
        `Esperienza "${experience.name || experienceId}" rimossa da admin e mobile. ` +
        `Intent Dialogflow eliminati: ${result.deletedIntentCount}/${result.matchedIntentCount}. ` +
        'Per un ripristino completo serve sia rimuovere il flag hidden sia ricreare gli intent associati.',
      );
    } catch (err) {
      console.error('[ExperiencesNew] hide experience failed', err);
      const rawMessage = err instanceof Error ? err.message : String(err || '');
      setError(
        rawMessage
          ? `Rimozione esperienza non riuscita: ${rawMessage}`
          : 'Rimozione esperienza non riuscita. Riprova.',
      );
    } finally {
      setDeletingExperienceId(null);
    }
  };

  const tabs = [
    { id: 'base' as const, label: 'Informazioni base' },
    { id: 'chatbot' as const, label: 'Genius Loci' },
    ...(!isCreating ? [{ id: 'prompt' as const, label: 'Prompt & Config.' }] : []),
    ...(!isCreating ? [{ id: 'knowledge' as const, label: 'Knowledge & Media' }] : []),
    { id: 'poi' as const, label: 'POI AI Mappa' },
    { id: 'media' as const, label: 'Copertina & Media' },
  ];
  const isPoiModuleLocked = !hasPersistedExperienceId;

  return (
    <div className="space-y-7">
      {!editing && (
        <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Esperienze</h1>
          <p className="text-text-secondary mt-1">Gestisci le esperienze culturali della piattaforma Glooci</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2 text-sm"
            onClick={() => load(undefined, { forceRefresh: true })}
            disabled={loading}
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Aggiorna
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={openCreator}
          >
            <Plus size={16} /> Nuova esperienza
          </button>
        </div>
      </div>

      <div className="card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <GraduationCap size={15} className="text-primary" />
              Percorso laboratorio scuola
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              Sequenza consigliata: crea esperienza, aggiungi intent principali, poi rifinisci il prompt IA.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <button
              type="button"
              onClick={openCreator}
              className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
            >
              1. Nuova esperienza
            </button>
            <Link
              to="/interazioni"
              className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
            >
              2. Intent
            </Link>
            <button
              type="button"
              onClick={() => {
                const firstExperience = filtered[0] || experiences[0];
                if (firstExperience) {
                  openEditor(firstExperience);
                  setActiveTab('prompt');
                }
              }}
              className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
            >
              3. Prompt finale IA
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-5">
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={18} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome, città, descrizione o Genius Loci…"
            className="w-full rounded-lg border border-border pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary">
              <X size={16} />
            </button>
          )}
        </div>
        {query && (
          <p className="text-xs text-text-secondary mt-2">{filtered.length} risultat{filtered.length !== 1 ? 'i' : 'o'} per "{query}"</p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-error flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
          <button type="button" onClick={() => setError(null)} className="ml-auto"><X size={16} /></button>
        </div>
      )}


      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {loading
          ? Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="card overflow-hidden animate-pulse w-full max-w-[430px] mx-auto">
                <div className="aspect-[3/4] bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-5 w-2/3 bg-gray-200 rounded" />
                  <div className="h-4 w-1/3 bg-gray-200 rounded" />
                  <div className="h-12 bg-gray-200 rounded" />
                </div>
              </div>
            ))
          : filtered.length === 0
            ? (
              <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
                <Globe size={48} className="text-text-secondary/30 mb-4" />
                <h3 className="text-lg font-semibold text-text-secondary mb-2">
                  {query ? 'Nessun risultato trovato' : 'Nessuna esperienza ancora'}
                </h3>
                <p className="text-sm text-text-secondary max-w-sm">
                  {query ? `Prova a modificare la ricerca.` : 'Crea la tua prima esperienza culturale cliccando su "Nuova esperienza".'}
                </p>
              </div>
            )
            : filtered.map((exp) => {
                const coverCandidates = resolveExperienceCoverCandidates(exp);
                const avatarUrl = resolveAvatarUrl(exp.avatar);
                const safeAvatar = avatarUrl && !brokenMediaUrls[avatarUrl] ? avatarUrl : null;
                const safeCover = coverCandidates.find((url) => !brokenMediaUrls[url])
                  || safeAvatar
                  || null;
                const flowStatus = computeExperienceFlowStatus(exp, [], {
                  includeIntentChecks: false,
                  includePromptCheck: false,
                });
                return (
                  <div key={exp.id} className="card overflow-hidden flex flex-col group hover:shadow-lg transition-shadow duration-200 w-full max-w-[430px] mx-auto">
                    {/* Cover */}
                    <div className="relative aspect-[3/4] bg-gradient-to-br from-primary/10 via-accent/10 to-tertiary/10 overflow-hidden flex items-center justify-center">
                      {safeCover ? (
                        <img
                          key={safeCover}
                          src={safeCover}
                          alt=""
                          className="h-full w-full object-contain object-top"
                          onError={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            img.style.display = 'none';
                            const failedUrl = img.currentSrc || img.src;
                            if (failedUrl) {
                              setBrokenMediaUrls((prev) => (prev[failedUrl] ? prev : { ...prev, [failedUrl]: true }));
                            }
                          }}
                        />
                      ) : (
                        <div className="h-full w-full flex flex-col items-center justify-center gap-2">
                          <ImageIcon size={36} className="text-primary/30" />
                          <span className="text-xs text-text-secondary/50">Nessuna copertina</span>
                        </div>
                      )}
                      <div className="absolute top-3 right-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium shadow-sm backdrop-blur-sm ${exp.isPublished ? 'bg-green-500/90 text-white' : 'bg-gray-800/70 text-white'}`}>
                          {exp.isPublished ? 'Pubblica' : 'Bozza'}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        {safeAvatar ? (
                          <img key={safeAvatar} src={safeAvatar} alt={exp.nomeChatbot || ''} className="w-9 h-9 rounded-full object-cover object-top border-2 border-white shadow shrink-0"
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              const failedUrl = img.currentSrc || img.src;
                              if (failedUrl) {
                                setBrokenMediaUrls((prev) => (prev[failedUrl] ? prev : { ...prev, [failedUrl]: true }));
                              }
                              img.style.display = 'none';
                            }}
                          />
                        ) : (
                          exp.nomeChatbot && (
                            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0 text-primary font-bold text-sm">
                              {exp.nomeChatbot.charAt(0).toUpperCase()}
                            </div>
                          )
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-text-primary leading-tight truncate">{exp.name}</h3>
                          {exp.city && (
                            <p className="text-xs text-text-secondary flex items-center gap-1 mt-0.5">
                              <MapPin size={11} /> {exp.city}
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-text-secondary line-clamp-2 flex-1 mb-3">
                        {exp.description || 'Nessuna descrizione ancora disponibile.'}
                      </p>

                      <div className="rounded-lg border border-border bg-gray-50 px-2.5 py-2 mb-3">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-text-secondary">Completezza setup</span>
                          <span className="font-semibold text-text-primary">{flowStatus.score}%</span>
                        </div>
                        <div className="h-1.5 bg-white rounded-full mt-1.5 overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${flowStatus.score}%` }} />
                        </div>
                      </div>

                      {/* Meta chips */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {exp.nomeChatbot && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            🤖 {exp.nomeChatbot}
                          </span>
                        )}
                        {buildExperienceFlagChips(exp).map((chip) => (
                          <span
                            key={chip.key}
                            className={`text-xs px-2 py-0.5 rounded-full ${chip.className}`}
                          >
                            {chip.label}
                          </span>
                        ))}
                      </div>

                      <button
                        type="button"
                        className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary hover:text-white transition-colors"
                        onClick={() => openEditor(exp)}
                      >
                        <Edit3 size={14} /> Modifica
                        <ChevronRight size={14} className="ml-auto" />
                      </button>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <Link
                          to={`/interazioni?experienceId=${exp.id}`}
                          className="text-center text-xs rounded-lg border border-border px-2 py-1.5 text-text-secondary hover:bg-gray-50"
                        >
                          Intent
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            openEditor(exp);
                            setActiveTab('prompt');
                          }}
                          className="text-center text-xs rounded-lg border border-border px-2 py-1.5 text-text-secondary hover:bg-gray-50"
                        >
                          Prompt
                        </button>
                      </div>
                      <button
                        type="button"
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 text-xs font-medium text-error border border-error/30 rounded-lg px-3 py-1.5 hover:bg-error hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={() => void onDeleteSingleExperience(exp)}
                        disabled={deletingExperienceId === exp.id}
                      >
                        <Trash2 size={13} />
                        {deletingExperienceId === exp.id ? 'Eliminazione…' : 'Elimina esperienza'}
                      </button>
                    </div>
                  </div>
                );
              })}
      </div>
        </>
      )}

      {/* Edit / Create Page */}
      {editing && (
        <div className="-m-6 min-h-[calc(100vh-3.5rem)] bg-white pb-28">
          <div className="w-full flex flex-col">
            {/* Page header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div>
                <h2 className="text-xl font-bold text-text-primary">
                  {isCreating ? '✨ Nuova esperienza' : `Modifica — ${editing.name || `#${editing.id}`}`}
                </h2>
                <p className="text-xs text-text-secondary mt-0.5">
                  {isCreating ? 'Compila i campi principali e salva la nuova esperienza.' : `Esperienza #${editing.id} · ultima modifica dati piattaforma`}
                </p>
                {!isCreating && editing.id > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <Link
                      to={`/interazioni?experienceId=${editing.id}`}
                      className="text-[11px] rounded-md border border-border px-2 py-1 hover:bg-gray-50 text-text-secondary"
                    >
                      Vai a Intent
                    </Link>
                  </div>
                )}
              </div>
              <button type="button" onClick={closeEditorModal} className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary" title="Torna alla lista">
                <X size={20} />
              </button>
            </div>

            {saveFeedback && (
              <div className="px-6 pt-4 shrink-0">
                <div className="flex flex-col gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-green-900 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-start gap-3">
                    <Save size={18} className="mt-0.5 shrink-0" />
                    <p className="text-sm font-medium">{saveFeedback}</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-green-300 bg-white px-3 py-2 text-sm font-semibold text-green-900 hover:bg-green-100"
                    onClick={closeEditorModal}
                  >
                    Torna alle esperienze
                  </button>
                </div>
              </div>
            )}

            {/* Tabs */}
            {isCreating && (
              <div className="px-6 pt-4 shrink-0">
                <div className="rounded-xl border border-primary/25 bg-primary/5 p-4">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-3">
                    <Sparkles size={16} />
                    Compila automaticamente con IA
                  </div>
                
                  <div className="flex flex-col md:flex-row gap-2">
                    <input
                      type="text"
                      className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="Inserisci un luogo geografico (es. Agropoli, Salerno)"
                      value={autoFillLocation}
                      onChange={(e) => setAutoFillLocation(e.target.value)}
                      disabled={autoFilling}
                    />
                    <button
                      type="button"
                      className="btn-outline whitespace-nowrap inline-flex items-center justify-center gap-2"
                      onClick={onAutoFillWithIa}
                      disabled={autoFilling}
                    >
                      <Sparkles size={14} />
                      {autoFilling ? 'Compilazione…' : 'Compila campi'}
                    </button>
                  </div>
                  {autoFillError && (
                    <p className="text-xs text-error mt-2">{autoFillError}</p>
                  )}
                </div>
              </div>
            )}

            <div className="px-6 pt-4 shrink-0">
              <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`min-w-[150px] flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}
                  >
                    <span className="inline-flex items-center justify-center gap-1.5">
                      {tab.label}
                      {tab.id === 'knowledge' && (
                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-amber-800">
                          BETA
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="px-6 py-4 space-y-4">
              <div className="rounded-xl border border-border bg-gray-50 p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-text-primary">Stato setup esperienza</span>
                  <span className="text-text-secondary">
                    {editFlowStatus.score}% ({editFlowStatus.completedRequired}/{editFlowStatus.totalRequired})
                  </span>
                </div>
                {editFlowStatus.missingRequired.length > 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    Campi da completare: {editFlowStatus.missingRequired.map((item) => item.label).join(', ')}.
                  </p>
                )}
              </div>
              <div className="rounded-xl border border-primary/25 bg-primary/5 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-primary mb-2">
                  <Brain size={14} />
                  APE 1.0 nel workflow esperienza (L0-L6)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L0 Globale</strong> · regole centrali {apeConfig.level0Enabled ? 'attive' : 'fallback standard'}</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L1 Contesto</strong> · tab “Informazioni base” (nome, città, descrizione)</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L2 Identità</strong> · tab “Genius Loci” (nome + personalità/Jung)</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L3 Voce/Stile</strong> · voce e registro in “Prompt & Configurazione”</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L4 Interazione</strong> · intent e POI geolocalizzati</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L5 Vincoli</strong> · prompt esperienza + base conoscenza (opzionale)</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2 md:col-span-2"><strong>L6 Adattamento utente</strong> · runtime mobile/web (età, livello, lingua, ADHD) {apeConfig.level6Enabled ? 'attivo' : 'fallback standard'}</div>
                </div>
              </div>
              {activeTab === 'base' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block md:col-span-2">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Messaggio iniziale (intent start/welcome)</span>
                      <textarea
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[90px] resize-y"
                        value={String(editForm.frase_avvio_percorso || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, frase_avvio_percorso: e.target.value }))}
                        placeholder="Messaggio mostrato quando l'utente avvia la chat (evento start/input.welcome)."
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Nome esperienza <span className="text-error">*</span></span>
                      <input
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        value={String(editForm.name || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Es. I Bronzi di Riace"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Città</span>
                      <input
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        value={String(editForm.city || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))}
                        placeholder="Es. Reggio Calabria"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-sm font-medium text-text-secondary block mb-1.5">Descrizione</span>
                    <textarea
                      className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[120px] resize-y"
                      value={String(editForm.description || '')}
                      onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Descrivi l'esperienza culturale in modo coinvolgente…"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-medium text-text-secondary block mb-1.5">Lunghezza risposte in chat</span>
                    <select
                      className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={normalizeResponseLength(editForm.responseLength)}
                      onChange={(e) => setEditForm((p) => ({
                        ...p,
                        responseLength: normalizeResponseLength(e.target.value),
                      }))}
                    >
                      <option value="short">Breve - fino a 70 parole</option>
                      <option value="medium">Media - fino a 120 parole</option>
                      <option value="long">Approfondita - fino a 220 parole</option>
                    </select>
                  </label>

                  <div className="rounded-xl border border-border p-4 bg-gray-50/60">
                    <p className="text-sm font-medium text-text-primary mb-1">Coordinate esperienza (mappa)</p>
                    <p className="text-xs text-text-secondary mb-3">
                      Compatibile con il flusso storico: puoi cercare un indirizzo, cliccare sulla mappa o impostare i valori manualmente.
                    </p>
                    <MapCoordinatePicker
                      latitude={editForm.latitude ?? null}
                      longitude={editForm.longitude ?? null}
                      onChange={(lat, lng) => setEditForm((p) => ({ ...p, latitude: lat, longitude: lng }))}
                      label="Posizione esperienza"
                    />
                  </div>

                  <div className="rounded-xl border border-border p-4 bg-white">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-sm font-medium text-text-primary">Flag esperienza e chip informative</p>
                        <p className="text-xs text-text-secondary mt-1">
                          Questi dati vengono letti dall&apos;app per costruire le chip nella pagina esperienza.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="text-sm font-medium text-text-secondary block mb-1.5">Durata stimata (minuti)</span>
                        <input
                          type="number"
                          min="0"
                          className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          value={editForm.duration ?? ''}
                          onChange={(e) => setEditForm((p) => ({
                            ...p,
                            duration: e.target.value === '' ? undefined : Number(e.target.value),
                          }))}
                          placeholder="Es. 45"
                        />
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium text-text-secondary block mb-1.5">Distanza stimata (km)</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          value={editForm.distance ?? ''}
                          onChange={(e) => setEditForm((p) => ({
                            ...p,
                            distance: e.target.value === '' ? undefined : Number(e.target.value),
                          }))}
                          placeholder="Es. 3"
                        />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                      {[
                        {
                          key: 'isKids',
                          label: 'Per famiglie / bambini',
                          checked: Boolean(editForm.isKids),
                        },
                        {
                          key: 'accessibility',
                          label: 'Accessibile',
                          checked: Boolean(editForm.accessibility),
                        },
                        {
                          key: 'isOnTheSite',
                          label: 'Esperienza sul posto',
                          checked: Boolean(editForm.isOnTheSite),
                        },
                        {
                          key: 'isAtHome',
                          label: 'Esperienza fruibile da casa',
                          checked: Boolean(editForm.isAtHome),
                        },
                      ].map((item) => (
                        <label
                          key={item.key}
                          className="flex items-center gap-3 rounded-lg border border-border bg-gray-50 px-3 py-2.5"
                        >
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={(e) => setEditForm((p) => ({
                              ...p,
                              [item.key]: e.target.checked,
                            }))}
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
                          />
                          <span className="text-sm text-text-primary">{item.label}</span>
                        </label>
                      ))}
                    </div>

                    <div className="mt-4">
                      <p className="text-xs font-medium text-text-secondary mb-2">Anteprima chip</p>
                      <div className="flex flex-wrap gap-2">
                        {(editForm.city
                          ? [{
                              key: 'city',
                              label: String(editForm.city),
                              className: 'bg-slate-100 text-slate-700',
                            }]
                          : []
                        )
                          .concat(buildExperienceFlagChips(editForm))
                          .map((chip) => (
                            <span
                              key={chip.key}
                              className={`text-xs px-2.5 py-1 rounded-full ${chip.className}`}
                            >
                              {chip.label}
                            </span>
                          ))}
                        {!editForm.city && buildExperienceFlagChips(editForm).length === 0 && (
                          <span className="text-xs text-text-secondary">
                            Nessuna chip aggiuntiva ancora configurata.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                </>
              )}

              {activeTab === 'chatbot' && (
                <>
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                    <p className="font-medium mb-1">💡 Cosa è il Genius Loci?</p>
                    <p>Il Genius Loci è la guida IA dell&apos;esperienza: un personaggio con una personalità specifica che accompagna l&apos;utente durante la visita.</p>
                  </div>

                  <label className="block">
                    <span className="text-sm font-medium text-text-secondary block mb-1.5">Nome del Genius Loci (chatbot)</span>
                    <input
                      className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={String(editForm.nomeChatbot || '')}
                      onChange={(e) => setEditForm((p) => ({ ...p, nomeChatbot: e.target.value }))}
                      placeholder="Es. Atena, Poseidone, Flora…"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-medium text-text-secondary block mb-1.5">Descrizione personalità</span>
                    <textarea
                      className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[100px] resize-y"
                      value={String(editForm.tutorDescription || '')}
                      onChange={(e) => setEditForm((p) => ({ ...p, tutorDescription: e.target.value }))}
                      placeholder="Descrivi chi è e come si comporta questo personaggio…"
                    />
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Messaggio intermedio</span>
                      <textarea
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[100px] resize-y"
                        value={String(editForm.messIntermedio || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, messIntermedio: e.target.value }))}
                        placeholder="Messaggio mostrato a metà percorso…"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Messaggio finale</span>
                      <textarea
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[100px] resize-y"
                        value={String(editForm.messFinale || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, messFinale: e.target.value }))}
                        placeholder="Messaggio di congedo a fine visita…"
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Registro linguistico</span>
                      <input
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        value={String(editForm.tutorLanguageRegister || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, tutorLanguageRegister: e.target.value }))}
                        placeholder="Es. formale, informale, arcaico…"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-text-secondary block mb-1.5">Epoca storica</span>
                      <input
                        className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        value={String(editForm.tutorEpoch || '')}
                        onChange={(e) => setEditForm((p) => ({ ...p, tutorEpoch: e.target.value }))}
                        placeholder="Es. Antica Grecia, Rinascimento…"
                      />
                    </label>
                  </div>
                </>
              )}

              {activeTab === 'prompt' && !isCreating && editing.id > 0 && (
                <PromptsGeniusLoci
                  embedded
                  embeddedExperienceId={Number(editing.id)}
                  onExperienceDraftChange={(draftExperience) => {
                    const editingId = Number(editing.id);
                    setEditing((prev) => (
                      prev && Number(prev.id) === editingId
                        ? { ...prev, ...draftExperience }
                        : prev
                    ));
                    setEditForm((prev) => ({
                      ...prev,
                      nomeChatbot: draftExperience.nomeChatbot !== undefined
                        ? String(draftExperience.nomeChatbot || '')
                        : prev.nomeChatbot,
                      messIntermedio: draftExperience.messIntermedio !== undefined
                        ? String(draftExperience.messIntermedio || '')
                        : prev.messIntermedio,
                      messFinale: draftExperience.messFinale !== undefined
                        ? String(draftExperience.messFinale || '')
                        : prev.messFinale,
                      tutorPersonality: draftExperience.tutorPersonality !== undefined
                        ? String(draftExperience.tutorPersonality || '')
                        : prev.tutorPersonality,
                      tutorKnowledge: draftExperience.tutorKnowledge !== undefined
                        ? String(draftExperience.tutorKnowledge || '')
                        : prev.tutorKnowledge,
                      geminiPrompt: draftExperience.geminiPrompt !== undefined
                        ? String(draftExperience.geminiPrompt || '')
                        : prev.geminiPrompt,
                      recoveryPrompt: draftExperience.recoveryPrompt !== undefined
                        ? String(draftExperience.recoveryPrompt || '')
                        : prev.recoveryPrompt,
                      voce: draftExperience.voce !== undefined ? draftExperience.voce : prev.voce,
                      idPersonality: draftExperience.idPersonality !== undefined
                        ? draftExperience.idPersonality
                        : prev.idPersonality,
                    }));
                    setExperiences((prev) => prev.map((item) => (
                      Number(item.id) === editingId
                        ? { ...item, ...draftExperience }
                        : item
                    )));
                  }}
                  onExperienceUpdated={(updatedExperience) => {
                    setEditing((prev) => (
                      prev && Number(prev.id) === Number(updatedExperience.id)
                        ? { ...prev, ...updatedExperience }
                        : prev
                    ));
                    setEditForm((prev) => ({
                      ...prev,
                      nomeChatbot: String(updatedExperience.nomeChatbot || prev.nomeChatbot || ''),
                      messIntermedio: String(updatedExperience.messIntermedio || prev.messIntermedio || ''),
                      messFinale: String(updatedExperience.messFinale || prev.messFinale || ''),
                      tutorPersonality: String(updatedExperience.tutorPersonality || prev.tutorPersonality || ''),
                      tutorKnowledge: String(updatedExperience.tutorKnowledge || prev.tutorKnowledge || ''),
                      geminiPrompt: String(updatedExperience.geminiPrompt || prev.geminiPrompt || ''),
                      recoveryPrompt: String(updatedExperience.recoveryPrompt || prev.recoveryPrompt || ''),
                      voce: updatedExperience.voce ?? prev.voce,
                      idPersonality: updatedExperience.idPersonality ?? prev.idPersonality,
                    }));
                    setExperiences((prev) => prev.map((item) => (
                      Number(item.id) === Number(updatedExperience.id)
                        ? { ...item, ...updatedExperience }
                        : item
                    )));
                  }}
                />
              )}

              {activeTab === 'knowledge' && !isCreating && editing.id > 0 && (
                <ExperienceKnowledgeMediaPanel
                  experience={{
                    ...editing,
                    ...editForm,
                    id: Number(editing.id),
                  } as LegacyExperience}
                />
              )}

              {activeTab === 'poi' && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-primary/25 bg-primary/5 p-4">
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                      <Brain size={16} />
                      POI esperienza
                    </div>
                    <p className="text-xs text-text-secondary">
                      Apri un POI, correggi testi e posizione sulla mappa, poi premi Salva POI. Il salvataggio dell&apos;esperienza resta separato.
                    </p>
                  </div>

                  {isPoiModuleLocked && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                      Per creare intent POI serve un ID esperienza. Salva prima la nuova esperienza, poi riaprila e usa questo modulo.
                    </div>
                  )}

                  <label className="block">
                    <span className="text-sm font-medium text-text-secondary block mb-1.5">
                      Prompt POI (opzionale)
                    </span>
                    <textarea
                      className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[90px] resize-y"
                      value={poiPrompt}
                      onChange={(e) => setPoiPrompt(e.target.value)}
                      placeholder="Es. Crea POI storici e gastronomici vicini al percorso, con priorità a luoghi realmente visitabili."
                      disabled={isPoiModuleLocked}
                    />
                  </label>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-outline inline-flex items-center gap-2"
                      onClick={onGeneratePois}
                      disabled={isPoiModuleLocked || poiGenerating}
                    >
                      <Bot size={15} />
                      {poiGenerating ? 'Generazione POI…' : 'Genera POI con IA'}
                    </button>
                    <button
                      type="button"
                      className="btn-outline inline-flex items-center gap-2"
                      onClick={addManualPoiDraft}
                      disabled={isPoiModuleLocked || poiSaving}
                    >
                      <Plus size={15} />
                      Nuovo POI
                    </button>
                  </div>

                  <div className="rounded-lg border border-border bg-white px-3 py-2 text-xs text-text-secondary">
                    I POI salvati diventano intent geolocalizzati usati dalla chat e dalla mappa mobile. Le bozze non salvate restano visibili in questa schermata mentre lavori.
                  </div>

                  {poiError && (
                    <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
                      {poiError}
                    </div>
                  )}
                  {poiInfo && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                      {poiInfo}
                    </div>
                  )}

                  {!isPoiModuleLocked && poiDrafts.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border bg-white p-6 text-sm text-text-secondary">
                      Genera POI con IA o crea un nuovo POI manuale, poi aprilo e scegli la posizione sulla mappa.
                    </div>
                  )}

                  {!isPoiModuleLocked && poiDrafts.length > 0 && (
                    <div className="grid grid-cols-1 2xl:grid-cols-[24rem_1fr] gap-4">
                      <div className="space-y-2 max-h-[calc(100vh-22rem)] overflow-y-auto pr-1">
                        {poiDrafts.map((poi) => (
                          <article
                            key={poi.id}
                            className={`rounded-xl border bg-white p-3 transition ${
                              selectedPoiMapId === poi.id ? 'border-primary shadow-sm' : 'border-border'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <button
                                type="button"
                                onClick={() => setSelectedPoiMapId(poi.id)}
                                className="flex-1 min-w-0 text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <p className="truncate text-sm font-semibold text-text-primary">
                                    {poi.name || 'POI senza nome'}
                                  </p>
                                  {poi.dirty && (
                                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                      Da salvare
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 line-clamp-2 text-xs text-text-secondary">
                                  {poi.address || poi.summary || 'Apri per completare dettagli e coordinate.'}
                                </p>
                                <p className="mt-2 text-[11px] text-text-muted">
                                  {poi.latitude != null && poi.longitude != null
                                    ? `${poi.latitude}, ${poi.longitude}`
                                    : 'Coordinate mancanti'}
                                </p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedPoiMapId(poi.id)}
                                className={`text-xs px-2 py-1 rounded-md border shrink-0 ${
                                  selectedPoiMapId === poi.id
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border text-text-secondary hover:text-text-primary'
                                }`}
                              >
                                Apri
                              </button>
                              <button
                                type="button"
                                onClick={() => void removePoiDraft(poi)}
                                disabled={poiSaving || poiDeletingId === poi.id}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Elimina questo POI"
                              >
                                <Trash2 size={13} />
                                {poiDeletingId === poi.id ? 'Elimino…' : 'Elimina'}
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>

                      <div className="rounded-xl border border-border bg-white p-4">
                        {selectedPoi ? (
                          <div className="space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-text-primary">Dettaglio POI</p>
                                <p className="text-xs text-text-secondary">
                                  Modifica i dati, seleziona il punto sulla mappa e salva solo questo POI.
                                </p>
                              </div>
                              <button
                                type="button"
                                className="btn-primary inline-flex items-center gap-2"
                                onClick={() => void onSaveCurrentPoi()}
                                disabled={poiSaving}
                              >
                                <Save size={15} />
                                {poiSaving ? 'Salvataggio…' : 'Salva POI'}
                              </button>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <label className="block">
                                <span className="text-sm font-medium text-text-secondary block mb-1.5">Nome POI</span>
                                <input
                                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-text-primary"
                                  value={selectedPoi.name}
                                  onChange={(e) => updatePoiDraft(selectedPoi.id, { name: e.target.value })}
                                  placeholder="Nome del punto di interesse"
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm font-medium text-text-secondary block mb-1.5">Indirizzo</span>
                                <input
                                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm"
                                  value={selectedPoi.address}
                                  onChange={(e) => updatePoiDraft(selectedPoi.id, { address: e.target.value })}
                                  placeholder="Indirizzo o riferimento"
                                />
                              </label>
                            </div>

                            <label className="block">
                              <span className="text-sm font-medium text-text-secondary block mb-1.5">Risposta in chat</span>
                              <textarea
                                className="w-full rounded-lg border border-border px-3 py-2.5 text-sm min-h-[110px] resize-y"
                                value={selectedPoi.summary}
                                onChange={(e) => updatePoiDraft(selectedPoi.id, { summary: e.target.value })}
                                placeholder="Testo che l'assistente userà quando l'utente apre o chiede questo POI"
                              />
                            </label>

                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_12rem_12rem] gap-4">
                              <label className="block">
                                <span className="text-sm font-medium text-text-secondary block mb-1.5">Link Google Maps</span>
                                <input
                                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm"
                                  value={selectedPoi.mapsUrl}
                                  onChange={(e) => updatePoiDraft(selectedPoi.id, { mapsUrl: e.target.value })}
                                  placeholder="https://maps.google.com/..."
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm font-medium text-text-secondary block mb-1.5">Latitudine</span>
                                <input
                                  type="number"
                                  step="any"
                                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm"
                                  value={selectedPoi.latitude ?? ''}
                                  onChange={(e) => updatePoiDraft(selectedPoi.id, { latitude: e.target.value === '' ? null : Number(e.target.value) })}
                                  placeholder="41.000"
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm font-medium text-text-secondary block mb-1.5">Longitudine</span>
                                <input
                                  type="number"
                                  step="any"
                                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm"
                                  value={selectedPoi.longitude ?? ''}
                                  onChange={(e) => updatePoiDraft(selectedPoi.id, { longitude: e.target.value === '' ? null : Number(e.target.value) })}
                                  placeholder="14.000"
                                />
                              </label>
                            </div>

                            <MapCoordinatePicker
                              latitude={selectedPoi.latitude}
                              longitude={selectedPoi.longitude}
                              onChange={(lat, lng) => updatePoiDraft(selectedPoi.id, { latitude: lat, longitude: lng })}
                              label={`Coordinate POI: ${selectedPoi.name || 'POI'}`}
                            />

                            {selectedPoi.suggestedQueries.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {selectedPoi.suggestedQueries.slice(0, 6).map((question, idx) => (
                                  <span key={`${selectedPoi.id}-q-${idx}`} className="text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                    {question}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-text-secondary">
                            Apri un POI dall&apos;elenco per modificarlo e salvarlo.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'media' && (
                <div className="space-y-5">
                  <div
                    className="rounded-xl"
                    onDragOver={handleCoverDragOver}
                    onDragLeave={handleCoverDragLeave}
                    onDrop={handleCoverDrop}
                  >
                    <p className="text-sm font-medium text-text-primary mb-1">Immagine di copertina</p>
                    <p className="text-xs text-text-secondary mb-3">
                      L&apos;immagine viene mostrata come anteprima dell&apos;esperienza nell&apos;app e nella piattaforma.
                      Puoi trascinare un file qui o selezionarlo dal pulsante sotto.
                    </p>

                    {coverPreview ? (
                      <div
                        className={`relative mb-3 rounded-xl transition-[box-shadow,ring] ${
                          coverDropActive ? 'ring-2 ring-primary ring-offset-2 ring-offset-white' : ''
                        }`}
                      >
                        <img
                          src={coverPreview}
                          alt=""
                          draggable={false}
                          className={`w-full h-[34rem] object-contain rounded-xl border bg-white/60 transition-colors ${
                            coverDropActive ? 'border-primary border-2' : 'border-border'
                          }`}
                          onError={(e) => {
                            const el = e.currentTarget as HTMLImageElement;
                            el.style.display = 'none';
                            el.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-[34rem] rounded-xl border-2 border-dashed border-error/40 bg-error/5 flex items-center justify-center">
                          <div className="text-center">
                            <AlertCircle size={32} className="text-error/50 mx-auto mb-2" />
                            <p className="text-xs text-error/70">Immagine non accessibile</p>
                            <p className="text-xs text-text-secondary mt-1">Carica una nuova immagine su Glooci Platform</p>
                          </div>
                        </div>
                        {coverDropActive && (
                          <div className="pointer-events-none absolute inset-0 rounded-xl bg-primary/10 border-2 border-dashed border-primary flex flex-col items-center justify-center gap-2">
                            <Upload size={40} className="text-primary" />
                            <span className="text-sm font-medium text-primary">Rilascia per sostituire la copertina</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => { setCoverPreview(''); setCoverFile(null); setEditForm((p) => ({ ...p, image: '' })); }}
                          className="absolute top-2 right-2 p-1.5 bg-error rounded-full text-white shadow z-10"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`w-full h-[34rem] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 mb-3 px-4 text-center transition-colors ${
                          coverDropActive
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-gradient-to-br from-primary/5 to-accent/5 text-text-secondary'
                        }`}
                      >
                        <Upload size={36} className={coverDropActive ? 'text-primary' : 'text-text-secondary/30'} />
                        <span className={`text-sm font-medium ${coverDropActive ? 'text-primary' : 'text-text-secondary'}`}>
                          {coverDropActive ? "Rilascia l'immagine qui" : "Trascina un'immagine qui"}
                        </span>
                        <span className="text-xs text-text-secondary/80 max-w-sm">
                          PNG, JPG, WebP o altri formati immagine supportati dal browser
                        </span>
                      </div>
                    )}

                    <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-border bg-gray-50 px-4 py-2.5 text-sm text-text-primary hover:bg-gray-100 transition-colors w-full justify-center">
                      <ImageIcon size={15} />
                      {coverUploading ? 'Upload in corso su Glooci Platform…' : coverFile ? `Selezionato: ${coverFile.name}` : 'Carica nuova copertina su Glooci Platform'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) onCoverChange(f); }}
                      />
                    </label>

                    {coverFile && (
                      <p className="text-xs text-text-secondary mt-2 flex items-center gap-1">
                        <AlertCircle size={12} className="text-amber-500" />
                        L&apos;immagine verrà caricata su Glooci Platform al momento del salvataggio
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* English translation panel - persiste in Firestore override
                  per far funzionare la mobile app in modalita' inglese. */}
              <div className="mt-4">
                <ExperienceTranslationsPanel
                  experienceId={!isCreating ? Number(editing?.id || 0) : null}
                  sourceFieldsIt={{
                    experienceName: String(editForm.name || ''),
                    experienceDescription: String(editForm.description || ''),
                    nomeChatbot: String(editForm.nomeChatbot || ''),
                    tutorDescription: String(editForm.tutorDescription || ''),
                    tutorPersonality: String(editForm.tutorPersonality || ''),
                    tutorKnowledge: String((editForm as Record<string, unknown>).tutorKnowledge || ''),
                    tutorLanguageRegister: String((editForm as Record<string, unknown>).tutorLanguageRegister || ''),
                    tutorEpoch: String((editForm as Record<string, unknown>).tutorEpoch || ''),
                    geminiPrompt: String((editForm as Record<string, unknown>).geminiPrompt || ''),
                    recoveryPrompt: String(editForm.recoveryPrompt || ''),
                    fallbackMessage: String((editForm as Record<string, unknown>).fallbackMessage || ''),
                    welcomeMessage: String(editForm.frase_avvio_percorso || ''),
                    voiceCode: String((editForm as Record<string, unknown>).voce || ''),
                  }}
                />
              </div>
            </div>

            {/* Footer actions */}
            <div className="fixed bottom-4 right-6 left-[calc(var(--admin-sidebar-offset)+1.5rem)] z-[70] rounded-2xl border border-border bg-white/95 px-4 py-3 shadow-2xl backdrop-blur">
              <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="btn-outline" onClick={closeEditorModal}>
                  Annulla
                </button>
                {!isCreating && editing.id > 0 && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg border border-error/30 px-3 py-2 text-sm font-medium text-error hover:bg-error hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={() => void onDeleteSingleExperience(editing)}
                    disabled={deletingExperienceId === editing.id}
                  >
                    <Trash2 size={15} />
                    {deletingExperienceId === editing.id ? 'Eliminazione…' : 'Elimina esperienza'}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {error && <p className="text-xs text-error">{error}</p>}
                {!isCreating && (
                  <button
                    type="button"
                    className="btn-outline inline-flex items-center gap-2"
                    onClick={() => void onSave(!Boolean(editForm.isPublished))}
                    disabled={saving || coverUploading || deletingExperienceId === editing.id}
                  >
                    {Boolean(editForm.isPublished) ? 'Passa a bozza' : 'Pubblica'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-primary inline-flex items-center gap-2"
                  onClick={() => void onSave()}
                  disabled={saving || coverUploading || deletingExperienceId === editing.id}
                >
                  <Save size={16} />
                  {saving || coverUploading ? 'Salvataggio…' : isCreating ? 'Crea esperienza' : 'Salva modifiche'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperiencesNew;
